<script lang="ts">
  import { onDestroy, onMount, tick } from 'svelte';
  import Capture from './lib/capture/Capture.svelte';
  import { captureFromUrl, type CaptureResult } from './lib/capture/capture';
  import LiveMirror from './lib/capture/LiveMirror.svelte';
  import { canUseLiveMirror } from './lib/renderer/liveCapability';
  import { detectLips, disposeFaceLandmarker, type LipDetection } from './lib/tracking/faceLandmarker';
  import { renderLipOverlay } from './lib/rendering/lipOverlay';
  import { renderCheekOverlay } from './lib/rendering/cheekOverlay';
  import { renderEyeOverlay } from './lib/rendering/eyeOverlay';
  import { renderEyebrowOverlay } from './lib/rendering/eyebrowOverlay';
  import { renderSkinSmoothing } from './lib/rendering/skinOverlay';
  import { renderSkinFrequencySeparation } from './lib/rendering/skinFrequencySeparation';
  import { renderComplexionOverlay } from './lib/rendering/complexionOverlay';
  import { renderHairOverlay } from './lib/rendering/hairOverlay';
  import { needsHairMaskUpgrade } from './lib/rendering/hairRecolourPlan';
  import { runMatting, destroyMattingWorker } from './lib/semantic/mattingClient';
  import type { PersonAlpha } from './lib/rendering/hairAlphaMatte';
  import { HAIR_SHADES, hairShadeNeedsLift, hairShadeIsDark, type HairShade } from './lib/catalog/hairShades';
  import { refineMakeupComposite } from './lib/rendering/makeupRefinement';
  import { buildSemanticFaceMap, type SemanticFaceMap } from './lib/semantic/semanticFaceMap';
  import { buildBestSemanticFaceMap } from './lib/semantic/semanticProvider';
  import { warmOnnxSemanticParser, isOnnxSemanticParserReady } from './lib/semantic/onnxFaceParser';
  import SemanticDebugPanel from './lib/semantic/SemanticDebugPanel.svelte';
  import RendererDebug from './lib/renderer/RendererDebug.svelte';
  import { analyzeFaceQuality, type FaceQualityReport } from './lib/quality/faceQuality';
  import {
    analyzeFaceMorphology,
    analyzeLookMorphologyTuning,
    type FaceMorphologyReport,
    type LookMorphologyTuningReport
  } from './lib/quality/faceMorphology';
  import { detectSubtone, type SubtoneResult } from './lib/quality/subtone';
  import {
    analyzeLightingCalibration,
    type LightingCalibrationReport
  } from './lib/quality/lightingCalibration';
  import {
    analyzeSkinToneCalibration,
    type SkinToneCalibrationReport,
    type SkinDepthClass,
    type SkinChromaClass
  } from './lib/quality/skinToneCalibration';
  import {
    emptyVtoPerformanceTiming,
    roundedMs,
    vtoPerformanceWarnings,
    type VtoPerformanceTiming
  } from './lib/quality/vtoPerformance';
  import {
    findStyleById,
    findStyleByShadeId,
    loadStyles,
    resolveRenderable,
    type MakeupStyle
  } from './lib/catalog/catalog';
  import {
    loadCrossBrandLookMatches,
    brandLabel,
    matchQuality,
    type CrossBrandMatchesFile,
    type CrossBrandMatchItem,
    type CrossBrandComponentId,
    type MatchQuality
  } from './lib/catalog/crossBrandMatches';
  import { applyWatermark, canvasToBlob } from './lib/share/watermark';
  import { shareComposite } from './lib/share/share';
  import { canUseWebGL2Renderer, getOptimalResolution } from './lib/renderer/detection';
  import { destroyOnnxBridge } from './lib/semantic/onnxBridge';
  import { hapticLight, hapticMedium } from './lib/ui/haptic';
  import { createDoubleTapHandler } from './lib/ui/gestures';
  import { RendererBridge } from './lib/renderer/rendererBridge';
  import { packMaskAtlases } from './lib/renderer/TexturePacker';
  import { track } from './lib/analytics/analytics';
  import { resolveVtoLang, VTO_COPY, type VtoLang } from './lib/i18n/vtoCopy';
  import { localizeStyleForDisplay } from './lib/i18n/vtoStyleCopy';
  import {
    FREE_TRY_LIMIT,
    consumeVtoTry,
    getVtoAccessState,
    remainingFreeTries,
    type VtoAccessState
  } from './lib/access/vtoAccess';

  type Phase = 'landing' | 'capturing' | 'mirror' | 'processing' | 'result' | 'no-face' | 'paywall';
  type MakeupIntensity = 'soft' | 'glam' | 'bold';
  type ProcessingStage = 'detecting' | 'calibrating' | 'preparing' | 'adapting' | 'rendering' | 'refining';
  type PreviewMode = 'after' | 'before' | 'compare';
  type SemanticMode = 'balanced' | 'high';

  const SKIN_WORK_MAX_BALANCED = 1080;
  // Lowered 2000 → 1280 (2026-06-17): the 2000px skin-work canvas plus the ~7
  // full-res overlay canvases peaked past the iOS WKWebView canvas budget on
  // 6 GB iPhones (13 Pro), silently blanking the composite (black screen) and
  // crashing on re-render. 1280 keeps "high" semantic masks while fitting.
  const SKIN_WORK_MAX_HIGH = 1280;
  // Server-side "premium render" uploads a JPEG of the composed look (the
  // user's face) to /api/vto_render.php. Kept OFF for the Play launch so the
  // VTO is 100% on-device — keeps the Google Play Data Safety declaration at
  // "no data collected". Flip to true only when the premium render is a real
  // priority AND the Data Safety form is updated to declare photo upload.
  const PREMIUM_RENDER_ENABLED = false;
  const LOOK_CARD_BG = '#f4f5f7';
  const LOOK_CARD_INK = '#16181d';
  const LOOK_CARD_MUTED = '#4d5562';
  const LOOK_CARD_ACCENT = '#0c7a74';
  const LOOK_CARD_SURFACE = '#ffffff';

  let phase = $state<Phase>('landing');
  let lang = $state<VtoLang>('it');
  let styles = $state<MakeupStyle[]>([]);
  let selectedStyle = $state<MakeupStyle | null>(null);
  let selectedHairShade = $state<HairShade | null>(null);
  // MODNet portrait alpha for the current photo (strand-accurate hair coverage).
  // Reset on each new capture; computed lazily on first hair-shade pick.
  let hairPersonAlpha = $state<PersonAlpha | null>(null);
  let crossBrandMatches = $state<CrossBrandMatchesFile | null>(null);

  let snapshot: CaptureResult | null = null;
  let detection: LipDetection | null = null;
  let quality = $state<FaceQualityReport | null>(null);
  let morphology = $state<FaceMorphologyReport | null>(null);
  let lookMorphology = $state<LookMorphologyTuningReport | null>(null);
  let subtone = $state<SubtoneResult | null>(null);
  // Hair shades whose `suits` matches the selfie's detected subtone — surfaced
  // first and badged as "recommended for you".
  const recommendedHairIds = $derived.by(() => {
    const cls = subtone?.class;
    const ids = new Set<string>();
    if (cls) for (const s of HAIR_SHADES) if (s.suits === cls || s.suits === 'all') ids.add(s.id);
    return ids;
  });
  const sortedHairShades = $derived.by(() => {
    const rec = recommendedHairIds;
    return [...HAIR_SHADES].sort((a, b) => (rec.has(b.id) ? 1 : 0) - (rec.has(a.id) ? 1 : 0));
  });
  let lightingCalibration = $state<LightingCalibrationReport | null>(null);
  let skinToneCalibration = $state<SkinToneCalibrationReport | null>(null);
  let semanticFaceMap = $state<SemanticFaceMap | null>(null);
  let detectionCacheKey = $state<string | null>(null);
  let performanceTiming = $state<VtoPerformanceTiming | null>(null);
  let capturePerformanceTiming: VtoPerformanceTiming | null = null;

  let previewEl = $state<HTMLDivElement | null>(null);
  // Self-healing memory guard: MODNet (the ML hair matte) is a second ONNX runtime
  // that loads on top of the on-screen result and OOMs memory-tight iPhones (4 GB
  // 11 Pro). If a prior hair-colour change crashed at the 'matting' stage (detected
  // via the localStorage breadcrumb on the next load), this flag is set true and
  // pinned, so MODNet is skipped from then on — hair recolour falls back to the
  // (already-built) semantic hair mask. Capable devices never trip it and keep MODNet.
  let skipModnet = false;
  let beforeEl = $state<HTMLDivElement | null>(null);
  let compareEl = $state<HTMLDivElement | null>(null);
  let showOriginal = $state(false);
  let compareMode = $state(false);
  let sliderPos = $state(50);
  let sharing = $state(false);
  let cardSharing = $state(false);
  let premiumRendering = $state(false);
  let premiumRenderStatus = $state('');
  let premiumRenderError = $state('');
  let premiumRenderDataUrl = $state('');
  let premiumRenderMode = $state('');
  let premiumRenderJobId = $state('');
  let detectionError = $state('');
  let makeupIntensity = $state<MakeupIntensity>('glam');
  let debugMasks = $state(false);
  let semanticMode = $state<SemanticMode>('balanced');
  // Set only from the ?semantic= URL param (or debugMasks); when present it wins
  // over the per-flow default. null = no override, use the per-flow default.
  let semanticModeOverride = $state<SemanticMode | null>(null);
  let rendererDebug = $state(false);
  let bridge: RendererBridge | null = null;
  let webglBitmap: ImageBitmap | null = null;
  let webglRendering = $state(false);
  let webglResultCanvas: HTMLCanvasElement | null = null;
  let afterPreviewCanvas: HTMLCanvasElement | null = null;
  let lastRefinementAttributes: Record<string, string> = {};
  let processingStage = $state<ProcessingStage>('detecting');
  let recomposing = $state(false);
  let renderFallbackNotice = $state(false);
  let accessState = $state<VtoAccessState | null>(null);
  let paywallReason = $state('');
  let checkoutLoading = $state(false);
  let checkoutError = $state('');
  const copy = $derived(VTO_COPY[lang]);

  const FULL_FACE_PRESETS: Record<MakeupIntensity, {
    label: string;
    skinStrengthCap: number;
    skinFallbackOpacityCap: number;
    complexionOpacityScale: number;
    underEyeOpacityScale: number;
    cheekOpacityScale: number;
    cheekRadiusScale: number;
    cheekDrapeScale: number;
    eyeOpacityScale: number;
    eyeShapeTightness: number;
    eyeFeatherScale: number;
    eyeSparkleScale: number;
    lipOpacityScale: number;
    lipFinishScale: number;
    eyebrowOpacity: number;
  }> = {
    soft: {
      label: 'soft',
      skinStrengthCap: 0.22,
      skinFallbackOpacityCap: 0.13,
      complexionOpacityScale: 0.72,
      underEyeOpacityScale: 0.68,
      cheekOpacityScale: 0.72,
      cheekRadiusScale: 0.95,
      cheekDrapeScale: 0.9,
      eyeOpacityScale: 0.72,
      eyeShapeTightness: 0.35,
      eyeFeatherScale: 0.76,
      eyeSparkleScale: 0.52,
      lipOpacityScale: 0.62,
      lipFinishScale: 0.74,
      eyebrowOpacity: 0.08
    },
    glam: {
      label: 'Glam',
      skinStrengthCap: 0.36,
      skinFallbackOpacityCap: 0.22,
      complexionOpacityScale: 1,
      underEyeOpacityScale: 1,
      cheekOpacityScale: 1.34,
      cheekRadiusScale: 1.08,
      cheekDrapeScale: 1.04,
      eyeOpacityScale: 1.32,
      eyeShapeTightness: 0.24,
      eyeFeatherScale: 0.88,
      eyeSparkleScale: 0.92,
      lipOpacityScale: 0.90,
      lipFinishScale: 0.94,
      eyebrowOpacity: 0.14
    },
    bold: {
      label: 'bold',
      skinStrengthCap: 0.44,
      skinFallbackOpacityCap: 0.29,
      complexionOpacityScale: 1.18,
      underEyeOpacityScale: 1.16,
      cheekOpacityScale: 1.9,
      cheekRadiusScale: 1.18,
      cheekDrapeScale: 1.16,
      eyeOpacityScale: 1.95,
      eyeShapeTightness: 0.15,
      eyeFeatherScale: 1.02,
      eyeSparkleScale: 1.22,
      lipOpacityScale: 1.18,
      lipFinishScale: 1.08,
      eyebrowOpacity: 0.2
    }
  };

  const INTENSITY_ORDER: MakeupIntensity[] = ['soft', 'glam', 'bold'];

  const BEAUTY_PLAYERS = [
    { name: 'Kiko Milano', url: 'https://www.kikocosmetics.com/it-it/' },
    { name: 'Douglas', url: 'https://www.douglas.it/' },
    { name: 'Sephora', url: 'https://www.sephora.it/' },
    { name: 'MAC Cosmetics', url: 'https://www.maccosmetics.it/' }
  ];

  const CROSS_BRAND_COMPONENTS: Array<{ id: CrossBrandComponentId }> = [
    { id: 'lipstick' },
    { id: 'blush' },
    { id: 'eyeshadow' }
  ];

  let premiumRenderProgressTimer: number | null = null;
  let premiumRenderProgressIndex = 0;

  function currentPreset() {
    return FULL_FACE_PRESETS[makeupIntensity];
  }

  function onboardingStyle(): MakeupStyle | null {
    return selectedStyle ?? styles.find((style) => style.id === 'regina') ?? styles[0] ?? null;
  }

  function skinDepthLabel(depth: SkinDepthClass): string {
    return copy.skinDepth[depth];
  }

  function skinChromaLabel(chroma: SkinChromaClass): string {
    return copy.skinChroma[chroma];
  }

  function refreshAccessState(): VtoAccessState {
    accessState = getVtoAccessState();
    return accessState;
  }

  function isVtoLimitBypassed(): boolean {
    // Auto-bypass in dev mode; manual bypass via localStorage for local builds
    if (import.meta.env.DEV) return true;
    try {
      return window.localStorage.getItem('beautyiq_vto_debug_unlimited') === '1';
    } catch {
      return false;
    }
  }

  function showPaywall(reason: string) {
    const state = refreshAccessState();
    paywallReason = reason;
    checkoutError = '';
    phase = 'paywall';
    track('vto_paywall_shown', {
      reason,
      used: state.used,
      limit: state.limit
    });
  }

  function canStartTryFlow(): boolean {
    if (isVtoLimitBypassed()) return true;
    const state = refreshAccessState();
    if (remainingFreeTries(state) <= 0) {
      showPaywall('free_limit_reached');
      return false;
    }
    return true;
  }

  function consumeTryFor(style: MakeupStyle): boolean {
    if (isVtoLimitBypassed()) {
      // In bypass mode still track usage for analytics, but don't block
      track('vto_free_try_used', {
        style: style.id,
        used: accessState?.used ?? 0,
        remaining: 999,
        limit: accessState?.limit ?? FREE_TRY_LIMIT,
        bypassed: true
      });
      return true;
    }
    const result = consumeVtoTry();
    accessState = result.state;
    if (!result.allowed) {
      showPaywall('free_limit_reached');
      return false;
    }
    track('vto_free_try_used', {
      style: style.id,
      used: result.state.used,
      remaining: remainingFreeTries(result.state),
      limit: result.state.limit
    });
    return true;
  }

  function isTrustedStripeUrl(url: string): boolean {
    try {
      const u = new URL(url);
      return u.protocol === 'https:' &&
        (u.host === 'billing.stripe.com' || u.host === 'checkout.stripe.com');
    } catch {
      return false;
    }
  }

  async function fetchCsrfToken(): Promise<string> {
    const resp = await fetch('/api/csrf_token.php', { cache: 'no-store' });
    if (!resp.ok) throw new Error(`csrf:${resp.status}`);
    const data = await resp.json();
    if (!data.csrf_token) throw new Error('csrf_missing');
    return data.csrf_token;
  }

  async function startCheckout(): Promise<void> {
    checkoutLoading = true;
    checkoutError = '';
    try {
      const state = refreshAccessState();
      const csrfToken = await fetchCsrfToken();
      const resp = await fetch('/api/checkout.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          csrf_token: csrfToken,
          trial_id: state.visitorId,
          source: 'vto_paywall'
        })
      });
      const data = await resp.json();
      if (!resp.ok || !data.success || !data.checkout_url) {
        throw new Error(data.error || `checkout:${resp.status}`);
      }
      if (!isTrustedStripeUrl(data.checkout_url)) {
        throw new Error('checkout:untrusted_url');
      }
      track('vto_checkout_started', { used: state.used, limit: state.limit });
      window.location.href = data.checkout_url;
    } catch (error) {
      checkoutError = copy.paywall.checkoutError;
      track('vto_checkout_error', {
        reason: error instanceof Error ? error.message : 'unknown'
      });
      checkoutLoading = false;
    }
  }

  // Doppio-RAF wrapper: composeLookCanvas è synchronous e blocca il main
  // thread per ~1–2 s su mobile mid-tier. Senza un yield, il browser non
  // ha tempo di paint il loader prima del freeze. Il primo rAF cede al
  // browser per disegnare lo stato `recomposing = true`, il secondo
  // garantisce che il paint sia commit prima di mutare la rune.
  function nextPaint(): Promise<void> {
    return new Promise((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
    });
  }

  async function pickHairShade(shade: HairShade | null) {
    const next = shade && selectedHairShade?.id === shade.id ? null : shade;
    hapticLight();
    clearPremiumRender();
    webglBitmap = null;
    webglResultCanvas = null;
    afterPreviewCanvas = null;
    selectedHairShade = next;
    if (phase !== 'result' || !snapshot || !detection || !selectedStyle) return;
    recomposing = true;
    await nextPaint();
    // Lazy-upgrade: the recolour needs the ONNX hair mask. Balanced flows (all
    // demos, and any real capture whose ONNX parse lost the 3.5s race) lack it,
    // so a shade pick would be a silent no-op. Build a high-quality semantic
    // map on demand for the current photo — non-blocking: if it can't produce a
    // hair mask (e.g. ONNX unavailable on the device) we recompose as before.
    if (needsHairMaskUpgrade(next, semanticFaceMap)) {
      markCrashStage('hair-semantic');
      try {
        const upgraded = await buildBestSemanticFaceMap(snapshot.bitmap, detection, { mode: 'high' });
        if (upgraded?.masks?.hair) semanticFaceMap = upgraded;
      } catch {
        // Hair recolour is additive; never break the existing composite.
      }
    }
    // ML alpha-matte (MODNet): strand-accurate coverage that reads the
    // background in the gaps between flyaway strands and keeps low-contrast hair.
    // Photo-dependent, so compute once per snapshot and reuse across shades.
    // Non-blocking — on failure/timeout the render falls back to the heuristic.
    if (next && semanticFaceMap?.masks?.hair && !hairPersonAlpha && !skipModnet) {
      // Belt-and-suspenders: if the hair-mask upgrade above recreated the
      // face-parser worker, free it again so MODNet runs alone (no two
      // concurrent ONNX models → no OOM on memory-tight iPhones).
      destroyOnnxBridge();
      markCrashStage('matting');
      try {
        const m = await runMatting(snapshot.bitmap);
        hairPersonAlpha = { data: m.alpha, aw: m.aw, ah: m.ah };
      } catch {
        hairPersonAlpha = null;
      } finally {
        // L'alpha è cachata per-snapshot: il worker MODNet (runtime ORT + wasm
        // + modello) resterebbe residente per tutta la sessione mentre il
        // composite gira — è il picco che fa ancora crashare una volta l'11
        // Pro. Liberalo subito; runMatting lo ricrea se serve un nuovo scatto.
        destroyMattingWorker();
      }
    }
    markCrashStage('hair-compose');
    renderCurrent();
    clearCrashStage();
    await nextPaint();
    recomposing = false;
  }

  async function setMakeupIntensity(next: MakeupIntensity) {
    if (makeupIntensity === next) return;
    hapticLight();
    clearPremiumRender();
    webglBitmap = null;
    webglResultCanvas = null;
    afterPreviewCanvas = null;
    if (phase !== 'result') {
      makeupIntensity = next;
      return;
    }
    recomposing = true;
    await nextPaint();
    makeupIntensity = next;
    renderCurrent();
    await nextPaint();
    recomposing = false;
  }

  function previewMode(): PreviewMode {
    if (compareMode) return 'compare';
    return showOriginal ? 'before' : 'after';
  }

  async function setPreviewMode(next: PreviewMode) {
    hapticLight();
    compareMode = next === 'compare';
    showOriginal = next === 'before';
    if (next === 'compare') {
      sliderPos = 50;
    }
    if (phase === 'result') {
      await tick();
      if (compareMode) {
        renderBefore();
        renderCurrent();
      } else if (showOriginal) {
        renderOriginal();
      } else {
        renderCurrent();
      }
    }
  }

  function segmentedButtonClass(active: boolean): string {
    return active
      ? 'bg-brand-accent text-white shadow-sm'
      : 'text-brand-muted hover:bg-white/70 hover:text-brand-accent';
  }

  function premiumPresetForIntensity(intensity: MakeupIntensity): 'beauty_wow' | 'glam' {
    return intensity === 'bold' ? 'glam' : 'beauty_wow';
  }

  function processingCopy(stage: ProcessingStage) {
    return copy.processing[stage] ?? copy.processing.detecting;
  }

  function semanticModeLabel(): string {
    return semanticMode === 'high' ? copy.qualitySummary.semanticHigh : copy.qualitySummary.semanticBalanced;
  }

  function qualityStatusLabel(): string {
    if (!performanceTiming) return copy.qualitySummary.evaluating;
    const warnings = vtoPerformanceWarnings(performanceTiming);
    if (!warnings.length) return copy.qualitySummary.excellent;
    if (warnings.length === 1) return copy.qualitySummary.stable;
    return copy.qualitySummary.optimized;
  }

  function localEngineLabel(): string {
    if (!semanticFaceMap) return copy.qualitySummary.localMesh;
    if (semanticFaceMap.quality.fallbacks.some((item) => item.includes('face-parsing'))) {
      return copy.qualitySummary.localMeshCachedParser;
    }
    return semanticMode === 'high' ? copy.qualitySummary.advancedParsing : copy.qualitySummary.localMesh;
  }

  function qualityWarningText(warning: string): string {
    // I warning arrivano già localizzati da faceQuality/lighting/skinTone
    // (copyNow) — il gate lang==='it' mostrerebbe il generico a chi non è IT.
    return warning;
  }

  function applyVtoTimingAttributes(canvas: HTMLCanvasElement, timing: VtoPerformanceTiming): void {
    canvas.setAttribute('data-vto-total-ms', String(timing.totalMs));
    canvas.setAttribute('data-vto-render-ms', String(timing.renderMs));
    canvas.setAttribute('data-vto-semantic-ms', String(timing.semanticMs));
    canvas.setAttribute('data-vto-detection-ms', String(timing.detectionMs));
    canvas.setAttribute('data-vto-quality-ms', String(timing.qualityMs));
    canvas.setAttribute('data-vto-calibration-ms', String(timing.calibrationMs));
    canvas.setAttribute('data-vto-skin-ms', String(timing.skinMs));
    canvas.setAttribute('data-vto-complexion-ms', String(timing.complexionMs));
    canvas.setAttribute('data-vto-cheek-ms', String(timing.cheekMs));
    canvas.setAttribute('data-vto-eye-ms', String(timing.eyeMs));
    canvas.setAttribute('data-vto-lip-ms', String(timing.lipMs));
    canvas.setAttribute('data-vto-brow-ms', String(timing.browMs));
    canvas.setAttribute('data-vto-refinement-ms', String(timing.refinementMs));
  }

  async function showProcessingStage(stage: ProcessingStage): Promise<void> {
    processingStage = stage;
    await nextPaint();
  }

  function releaseSnapshotBitmap(result: CaptureResult | null): void {
    if (!result) return;
    try {
      result.bitmap.close();
    } catch {
      // Best-effort cleanup: some browsers may already have released it.
    }
  }

  // iOS frees a canvas's backing store the instant width/height hit 0, NOT at the
  // next GC — and GC never runs mid-synchronous-composite. composeLookCanvas chains
  // ~8 full-res canvases through `base`; left alone they all stay resident at the
  // same instant and OOM the 4 GB iPhone 11 Pro (test device). Release each
  // intermediate the moment the next stage has consumed it. Guarded to HTMLCanvas
  // only, so the reused snapshot ImageBitmap is never touched.
  function releaseCanvas(c: HTMLCanvasElement | ImageBitmap | null | undefined): void {
    if (!c || !(c instanceof HTMLCanvasElement)) return;
    try {
      c.width = 0;
      c.height = 0;
    } catch {
      // Best-effort: releasing the backing store must never break the composite.
    }
  }

  // iOS jetsam-kills the WebView with NO JS error (try/catch never fires), so the
  // only reliable crash signal is a synchronous localStorage write that survives
  // the process kill. Mark the heavy pipeline stage on entry; clear it on success.
  // onMount reports any stage left dangling → Umami tells us exactly where it died.
  function markCrashStage(stage: string): void {
    try {
      localStorage.setItem('vto_stage', stage);
    } catch {
      // private mode / storage disabled — diagnostics are best-effort.
    }
  }
  function clearCrashStage(): void {
    try {
      localStorage.removeItem('vto_stage');
    } catch {
      // ignore
    }
  }

  function replaceSnapshot(next: CaptureResult | null): void {
    if (snapshot && snapshot !== next) {
      releaseSnapshotBitmap(snapshot);
    }
    snapshot = next;
  }

  function averageLandmarkY(detection: LipDetection, indices: number[]): number | null {
    let total = 0;
    let count = 0;
    for (const index of indices) {
      const point = detection.landmarks[index];
      if (!point || !Number.isFinite(point.y)) continue;
      total += point.y;
      count += 1;
    }
    return count > 0 ? total / count : null;
  }

  function isUpsideDownDetection(detection: LipDetection): boolean {
    const eyeY = averageLandmarkY(detection, [33, 133, 159, 263, 362, 386]);
    const mouthY = averageLandmarkY(detection, [13, 14, 61, 291]);
    if (eyeY !== null && mouthY !== null && mouthY < eyeY - 0.03) return true;
    return Math.abs(detection.pose.roll) > 2.45;
  }

  async function rotateCaptureResult180(result: CaptureResult): Promise<CaptureResult> {
    const canvas = document.createElement('canvas');
    canvas.width = result.width;
    canvas.height = result.height;
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) throw new Error('2D context unavailable');
    ctx.translate(result.width, result.height);
    ctx.rotate(Math.PI);
    ctx.drawImage(result.bitmap, 0, 0);
    const bitmap = await createImageBitmap(canvas);
    return { bitmap, width: result.width, height: result.height };
  }

  function clearPremiumRender(): void {
    stopPremiumRenderProgress();
    premiumRenderStatus = '';
    premiumRenderError = '';
    premiumRenderDataUrl = '';
    premiumRenderMode = '';
    premiumRenderJobId = '';
  }

  function stopPremiumRenderProgress(): void {
    if (premiumRenderProgressTimer !== null) {
      window.clearInterval(premiumRenderProgressTimer);
      premiumRenderProgressTimer = null;
    }
  }

  function startPremiumRenderProgress(): void {
    stopPremiumRenderProgress();
    premiumRenderProgressIndex = 0;
    premiumRenderStatus = copy.premium.progress[0] ?? copy.premium.progress.at(-1) ?? '';
    premiumRenderProgressTimer = window.setInterval(() => {
      premiumRenderProgressIndex = Math.min(
        premiumRenderProgressIndex + 1,
        copy.premium.progress.length - 1
      );
      premiumRenderStatus = copy.premium.progress[premiumRenderProgressIndex] ?? copy.premium.progress.at(-1) ?? '';
    }, 1400);
  }

  function sleep(ms: number): Promise<void> {
    return new Promise((resolve) => window.setTimeout(resolve, ms));
  }

  function crossBrandMatchesFor(componentId: CrossBrandComponentId): CrossBrandMatchItem[] {
    if (!selectedStyle || !crossBrandMatches) return [];
    return (crossBrandMatches.matches[selectedStyle.id]?.[componentId] ?? []).slice(0, 4);
  }

  function hasCrossBrandMatches(): boolean {
    return CROSS_BRAND_COMPONENTS.some((c) => crossBrandMatchesFor(c.id).length > 0);
  }

  // Verified buy link for a cross-brand match. The matcher only writes a
  // product_url when it resolved a real merchant page (Kiko / MAC direct);
  // null/empty entries get NO button. We never synthesise or guess a URL.
  // Routed through the /go affiliate choke-point (go.php: Awin quando approvato,
  // fallback search Amazon-IT taggata, geo per non-IT) — assoluto perché nel
  // guscio nativo un /go relativo risolverebbe sul bundle capacitor:// e 404.
  // I link Amazon (già taggati /dp/) restano diretti: /go non li allow-lista.
  const GO_ENDPOINT = 'https://www.beautyiq.cloud/go';
  function isNativeShell(): boolean {
    const c = (window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor;
    return !!c?.isNativePlatform?.();
  }
  function matchBuyUrl(item: CrossBrandMatchItem): string | null {
    const url = item.product_url?.trim();
    if (!url) return null;
    try {
      const u = new URL(url);
      if (u.protocol !== 'https:') return null;
      if (/(^|\.)amazon\./i.test(u.host)) return u.href;
      const q = `${item.brand} ${item.shade_name}`.trim();
      return `${GO_ENDPOINT}?u=${encodeURIComponent(u.href)}&b=${encodeURIComponent(item.brand)}` +
        `&s=vto&lang=${encodeURIComponent(lang)}&q=${encodeURIComponent(q)}${isNativeShell() ? '&app=1' : ''}`;
    } catch {
      return null;
    }
  }

  function onMatchBuyClick(componentId: CrossBrandComponentId, item: CrossBrandMatchItem): void {
    hapticLight();
    track('affiliate_click', {
      look: selectedStyle?.id ?? '',
      component: componentId,
      brand: item.brand,
      shade: item.shade_name,
      delta_e: item.delta_e
    });
  }

  // /look/<id> SEO page exists for every style in the catalog (look.php reads
  // the same makeup-styles.json). Only surface the link when the selected
  // style's id is in the loaded set, so we never link to a missing page.
  const lookPageUrl = $derived.by<string | null>(() => {
    const id = selectedStyle?.id;
    if (!id || !styles.some((s) => s.id === id)) return null;
    const suffix = lang && lang !== 'it' ? `?lang=${lang}` : '';
    return `/look/${id}${suffix}`;
  });

  function onViewLookClick(): void {
    hapticLight();
    track('vto_view_look_click', { look: selectedStyle?.id ?? '' });
  }

  // Best (closest-ΔE) cross-brand match per component for the selected look —
  // the live "this look across brands" teaser surfaced inside the mirror hook.
  type CrossBrandTeaserEntry = { componentId: CrossBrandComponentId; item: CrossBrandMatchItem };
  const crossBrandTeaser = $derived<CrossBrandTeaserEntry[]>(
    CROSS_BRAND_COMPONENTS
      .map((c) => ({ componentId: c.id, item: crossBrandMatchesFor(c.id)[0] }))
      .filter((e): e is CrossBrandTeaserEntry => Boolean(e.item))
  );

  // Badge colour per perceptual match tier — greener = closer colour.
  const MATCH_QUALITY_CLASS: Record<MatchQuality, string> = {
    perfect: 'bg-emerald-100 text-emerald-700',
    excellent: 'bg-teal-100 text-teal-700',
    good: 'bg-amber-100 text-amber-700',
    approximate: 'bg-brand-bg text-brand-muted'
  };

  onMount(() => {
    refreshAccessState();
    // Warm the ONNX face parser while the user reads the guidance / takes the
    // selfie, so high-detail mode resolves from cache instead of waiting on the
    // first-load (~50 MB) download. Idempotent; no-op once loaded/loading.
    warmOnnxSemanticParser();
    // NOTE: MODNet (hair matte) is deliberately NOT warmed here. On the 4 GB
    // iPhone 11 Pro, keeping a second ONNX runtime resident through the makeup
    // composite is budget the composite peak can't spare. MODNet lazily loads on
    // the first hair-colour change (pickHairShade → runMatting), with the
    // heuristic matte as the non-blocking fallback while it compiles.
    // Report any pipeline stage left dangling by a previous crash. iOS kills the
    // WebView with no JS error, but a synchronous localStorage write survives the
    // process kill — so the last-marked stage tells us where it died.
    try {
      // Pin the MODNet-skip decision across reloads. If a previous run crashed
      // while running the hair matte, this device can't afford it — skip it for good.
      if (localStorage.getItem('vto_skip_modnet') === '1') skipModnet = true;
      const lastStage = localStorage.getItem('vto_stage');
      if (lastStage) {
        track('vto_crash_recovered', { stage: lastStage });
        // A crash during the ML hair matte → this device OOMs on MODNet. Disable it
        // permanently here; hair recolour uses the semantic mask instead (no crash).
        if (lastStage === 'matting') {
          skipModnet = true;
          localStorage.setItem('vto_skip_modnet', '1');
        }
        localStorage.removeItem('vto_stage');
      }
    } catch {
      // localStorage may be unavailable (private mode); diagnostics are optional.
    }
    const params = new URLSearchParams(window.location.search);
    const resolvedLang = resolveVtoLang(params);
    lang = resolvedLang;
    document.documentElement.lang = resolvedLang;
    const incomingStyle = params.get('style');
    const incomingShade = params.get('shade');
    const incomingSemantic = params.get('semantic');
    debugMasks = params.get('debugMasks') === '1';
    rendererDebug = params.get('rendererDebug') === '1';
    semanticModeOverride = incomingSemantic === 'high' || incomingSemantic === 'balanced'
      ? incomingSemantic
      : debugMasks ? 'high' : null;

    loadStyles().then((list) => {
      styles = list;
      const byStyle = findStyleById(list, incomingStyle);
      const byShade = findStyleByShadeId(list, incomingShade);
      if (byStyle) {
        selectedStyle = byStyle;
      } else if (byShade) {
        selectedStyle = byShade.style;
      }
    });

    loadCrossBrandLookMatches().then((matches) => {
      crossBrandMatches = matches;
    });
  });

  onDestroy(() => {
    stopPremiumRenderProgress();
    replaceSnapshot(null);
    disposeFaceLandmarker();
    bridge?.destroy();
    bridge = null;
    webglResultCanvas = null;
    afterPreviewCanvas = null;
    lastRefinementAttributes = {};
    destroyOnnxBridge();
  });

  const handlePreviewTap = createDoubleTapHandler(() => {
    if (phase === 'result' && !compareMode) {
      hapticMedium();
      setPreviewMode(showOriginal ? 'after' : 'before');
    }
  });

  async function pickStyle(style: MakeupStyle) {
    hapticLight();
    clearPremiumRender();
    webglBitmap = null;
    webglResultCanvas = null;
    afterPreviewCanvas = null;
    if (snapshot && detection) {
      if (!consumeTryFor(style)) return;
      selectedStyle = style;
      lookMorphology = analyzeLookMorphologyTuning(style, morphology);
      track('style_viewed', { style: style.id });
      phase = 'result';
      await tick();
      renderCurrent();
      track('style_rendered', { style: style.id });
    } else {
      if (!canStartTryFlow()) return;
      selectedStyle = style;
      lookMorphology = analyzeLookMorphologyTuning(style, morphology);
      track('style_viewed', { style: style.id });
      // No snapshot yet — go to capture; after snap we'll render this style.
      phase = 'capturing';
    }
  }

  async function handleCaptured(result: CaptureResult, requestedMode: SemanticMode = 'high') {
    clearPremiumRender();
    bridge?.destroy();
    bridge = null;
    webglBitmap = null;
    webglResultCanvas = null;
    afterPreviewCanvas = null;
    lastRefinementAttributes = {};
    replaceSnapshot(result);
    quality = null;
    morphology = null;
    lookMorphology = null;
    subtone = null;
    lightingCalibration = null;
    skinToneCalibration = null;
    semanticFaceMap = null;
    hairPersonAlpha = null;
    detectionCacheKey = null;
    capturePerformanceTiming = null;
    performanceTiming = null;
    renderFallbackNotice = false;
    let workingResult = result;
    phase = 'processing';
    // URL ?semantic= override wins; otherwise use the per-flow default
    // (real capture → high, demo → balanced).
    semanticMode = semanticModeOverride ?? requestedMode;
    processingStage = 'detecting';
    detectionError = '';
    track('try_on_started');
    try {
      const flowStart = performance.now();
      const timing = emptyVtoPerformanceTiming();
      let start = performance.now();
      detection = await detectLips(workingResult.bitmap);
      timing.detectionMs = roundedMs(performance.now() - start);
      if (detection && isUpsideDownDetection(detection)) {
        start = performance.now();
        const corrected = await rotateCaptureResult180(workingResult);
        const correctedDetection = await detectLips(corrected.bitmap);
        timing.detectionMs += roundedMs(performance.now() - start);
        if (correctedDetection) {
          replaceSnapshot(corrected);
          workingResult = corrected;
          detection = correctedDetection;
          track('capture_orientation_corrected', {
            reason: 'upside_down_face',
            roll: Math.round(detection.pose.roll * 100) / 100
          });
        } else {
          releaseSnapshotBitmap(corrected);
        }
      }
      if (!detection) {
        phase = 'no-face';
        track('detection_failed', { reason: 'no_face' });
        return;
      }
      await showProcessingStage('calibrating');
      start = performance.now();
      quality = analyzeFaceQuality(workingResult.bitmap, detection);
      morphology = analyzeFaceMorphology(detection);
      timing.qualityMs = roundedMs(performance.now() - start);
      start = performance.now();
      lightingCalibration = analyzeLightingCalibration(workingResult.bitmap, detection);
      skinToneCalibration = analyzeSkinToneCalibration(workingResult.bitmap, detection);
      timing.calibrationMs = roundedMs(performance.now() - start);
      if (quality.blocker) {
        // I blocker arrivano già localizzati da faceQuality (copyNow) — anche
        // i non-IT ricevono l'hint specifico, non più il fallback generico.
        detectionError = quality.blocker;
        phase = 'no-face';
        track('detection_failed', { reason: quality.blockerCode ?? 'quality' });
        return;
      }
      // Cold-cache + high mode: surface a dedicated "preparing engine" stage
      // while the ONNX model finishes loading; otherwise the normal stage.
      await showProcessingStage(
        semanticMode === 'high' && !isOnnxSemanticParserReady() ? 'preparing' : 'adapting'
      );
      start = performance.now();
      markCrashStage('semantic');
      try {
        semanticFaceMap = await buildBestSemanticFaceMap(workingResult.bitmap, detection, {
          mode: semanticMode
        });
      } catch (semanticError) {
        try {
          semanticFaceMap = await buildSemanticFaceMap(workingResult.bitmap, detection, { mode: 'fast' });
          semanticFaceMap.quality.fallbacks = [
            ...semanticFaceMap.quality.fallbacks,
            'consumer-safe semantic fallback'
          ];
          track('semantic_fallback_used', {
            reason: (semanticError as Error).message || 'semantic_exception'
          });
        } catch (fallbackError) {
          semanticFaceMap = null;
          track('semantic_fallback_failed', {
            reason: (fallbackError as Error).message || 'semantic_fallback_exception'
          });
        }
      }
      timing.semanticMs = roundedMs(performance.now() - start);
      // Free the face-parser ONNX worker now that the semantic map is built and
      // cached: re-renders reuse the stored map, and the heavy MODNet hair matte
      // (runMatting) must not coexist with the parser on memory-tight iPhones
      // (6 GB 13 Pro crashed on hair-colour change). Lazily recreated if a later
      // capture or hair-mask upgrade needs it.
      destroyOnnxBridge();
      start = performance.now();
      subtone = detectSubtone(workingResult.bitmap, detection);
      timing.calibrationMs += roundedMs(performance.now() - start);
      capturePerformanceTiming = {
        ...timing,
        totalMs: roundedMs(performance.now() - flowStart)
      };
      performanceTiming = capturePerformanceTiming;
      detectionCacheKey = `${workingResult.width}x${workingResult.height}`;
      if (!selectedStyle && styles.length > 0) {
        selectedStyle = styles.find((s) => s.id === 'regina') ?? styles[0] ?? null;
      }
      lookMorphology = selectedStyle
        ? analyzeLookMorphologyTuning(selectedStyle, morphology)
        : null;
      processingStage = 'rendering';
      if (selectedStyle && !consumeTryFor(selectedStyle)) {
        return;
      }
      await showProcessingStage('rendering');
      phase = 'result';
      await tick();
      markCrashStage('compose');
      renderCurrent();
      clearCrashStage();
      if (selectedStyle) {
        track('style_rendered', { style: selectedStyle.id });
      }
    } catch (err) {
      clearCrashStage();
      detectionError = (err as Error).message || copy.detectionError;
      phase = 'no-face';
      track('detection_failed', { reason: 'exception' });
    }
  }

  function getBridge(width: number, height: number): RendererBridge {
    if (bridge) return bridge;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    bridge = new RendererBridge(canvas, {
      onResult: (bitmap, _mode, frameTimeMs) => {
        webglBitmap = bitmap;
        webglRendering = false;
        showWebGLBitmap(bitmap);
        if (capturePerformanceTiming) {
          const timing = {
            ...capturePerformanceTiming,
            renderMs: Math.round(frameTimeMs),
            totalMs: roundedMs(
              (capturePerformanceTiming.detectionMs ?? 0) +
              (capturePerformanceTiming.qualityMs ?? 0) +
              (capturePerformanceTiming.calibrationMs ?? 0) +
              (capturePerformanceTiming.semanticMs ?? 0) +
              frameTimeMs
            )
          };
          capturePerformanceTiming = timing;
          performanceTiming = timing;
        }
        if (frameTimeMs > 33) {
          track('vto_thermal_warning', { frameTimeMs: Math.round(frameTimeMs) });
        }
      },
      onError: (reason) => {
        webglRendering = false;
      },
      onExport: (blob) => {
        // Handled inline via bridge.exportImage + polling if needed
      }
    });
    bridge.init(width, height, true);
    return bridge;
  }

  function showWebGLBitmap(bitmap: ImageBitmap) {
    if (!previewEl) return;
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(bitmap, 0, 0);
    if (performanceTiming) applyVtoTimingAttributes(canvas, performanceTiming);
    Object.entries(lastRefinementAttributes).forEach(([key, value]) => {
      canvas.setAttribute(key, value);
    });
    canvas.setAttribute('data-vto-webgl', 'true');
    canvas.style.width = '100%';
    canvas.style.height = 'auto';
    canvas.style.display = 'block';
    webglResultCanvas = canvas;
    if (phase === 'result' && !showOriginal && !compareMode) {
      afterPreviewCanvas = canvas;
      previewEl.replaceChildren(canvas);
    }
  }

  function hexToRgbTuple(hex: string): [number, number, number] {
    const h = hex.replace('#', '');
    return [
      parseInt(h.slice(0, 2), 16),
      parseInt(h.slice(2, 4), 16),
      parseInt(h.slice(4, 6), 16)
    ];
  }

  function finishToNumber(finish: string): number {
    const f = finish.toLowerCase();
    if (f === 'matte') return 0;
    if (f === 'satin') return 1;
    if (f === 'glossy' || f === 'shimmer') return 2;
    if (f === 'metallic') return 3;
    return 1;
  }

  async function renderWebGLMakeup() {
    if (!snapshot || !detection || !selectedStyle || !semanticFaceMap) return;
    try {
      const b = getBridge(snapshot.width, snapshot.height);
      const atlases = await packMaskAtlases(semanticFaceMap, hairPersonAlpha);
      const preset = currentPreset();
      const r = resolveRenderable(selectedStyle, null);
      const tuning = quality?.tuning;
      const morph = morphology?.tuning;
      const lookMorph = lookMorphology?.tuning;
      const light = lightingCalibration?.tuning;
      const skinTone = skinToneCalibration?.tuning;
      const st = subtone;

      // Sample the actual mean skin colour from the photo using the ONNX skin
      // mask. Falls back to Fitzpatrick II (light skin) when the mask is absent
      // — the fallback is only hit on the landmark-pseudo path which lacks a
      // real hair mask anyway.
      const w = snapshot.width;
      const h = snapshot.height;
      const sampleCanvas = document.createElement('canvas');
      sampleCanvas.width = w;
      sampleCanvas.height = h;
      const sctx = sampleCanvas.getContext('2d', { willReadFrequently: true });
      let skinMean: [number, number, number] = [218, 184, 166];
      if (sctx && semanticFaceMap?.masks?.skin) {
        sctx.drawImage(snapshot.bitmap, 0, 0);
        const src = sctx.getImageData(0, 0, w, h).data;
        const maskCtx = semanticFaceMap.masks.skin.getContext('2d', { willReadFrequently: true });
        if (maskCtx) {
          const mask = maskCtx.getImageData(0, 0, semanticFaceMap.masks.skin.width, semanticFaceMap.masks.skin.height).data;
          let r = 0; let g = 0; let b = 0; let count = 0;
          const scaleX = semanticFaceMap.masks.skin.width / w;
          const scaleY = semanticFaceMap.masks.skin.height / h;
          for (let i = 0; i < src.length; i += 64) {
            const px = (i / 4) % w;
            const py = Math.floor((i / 4) / w);
            const mx = Math.min(semanticFaceMap.masks.skin.width - 1, Math.floor(px * scaleX));
            const my = Math.min(semanticFaceMap.masks.skin.height - 1, Math.floor(py * scaleY));
            const maskIdx = (my * semanticFaceMap.masks.skin.width + mx) * 4 + 3;
            const alpha = (mask[maskIdx] ?? 0) / 255;
            if (alpha < 0.18) continue;
            r += src[i]!;
            g += src[i + 1]!;
            b += src[i + 2]!;
            count++;
          }
          if (count > 20) {
            skinMean = [Math.round(r / count), Math.round(g / count), Math.round(b / count)];
          }
        }
      }

      b.renderMakeup({
        source: snapshot.bitmap,
        faceAtlas: atlases.face,
        eyeAtlas: atlases.eye,
        detailAtlas: atlases.detail,
        linerAtlas: atlases.liner,
        hairAtlas: atlases.hair || undefined,
        width: w,
        height: h,
        skinStrength: Math.min(
          preset.skinStrengthCap,
          (tuning?.skinStrength ?? 0.4) * (light?.skinStrengthScale ?? 1) * (skinTone?.skinStrengthScale ?? 1)
        ),
        veilOpacity: 0.075 * preset.complexionOpacityScale * (light?.skinStrengthScale ?? 1) * (skinTone?.skinStrengthScale ?? 1),
        underEyeOpacity: 0.11 * preset.underEyeOpacityScale * (light?.skinStrengthScale ?? 1) * (skinTone?.skinStrengthScale ?? 1),
        skinMean,
        cheekColor: selectedStyle.kit?.blush ? hexToRgbTuple(selectedStyle.kit.blush.hex) : undefined,
        cheekOpacity: selectedStyle.kit?.blush
          ? preset.cheekOpacityScale * (tuning?.cheekOpacityScale ?? 1) * (morph?.cheekOpacityScale ?? 1) * (lookMorph?.cheekOpacityScale ?? 1) * (light?.cheekOpacityScale ?? 1) * (skinTone?.cheekOpacityScale ?? 1) * (st ? 1 + st.blushWarmthShift : 1)
          : 0,
        eyeColor: selectedStyle.kit?.eyeshadow ? hexToRgbTuple(selectedStyle.kit.eyeshadow.hex) : undefined,
        eyeOpacity: selectedStyle.kit?.eyeshadow
          ? preset.eyeOpacityScale * (tuning?.eyeOpacityScale ?? 1) * (morph?.eyeOpacityScale ?? 1) * (lookMorph?.eyeOpacityScale ?? 1) * (light?.eyeOpacityScale ?? 1) * (skinTone?.eyeOpacityScale ?? 1) * (st?.eyeOpacityGain ?? 1)
          : 0,
        eyeFinish: finishToNumber(selectedStyle.kit?.eyeshadow?.finish ?? 'matte'),
        eyeSparkleScale: preset.eyeSparkleScale * (tuning?.eyeSparkleScale ?? 1) * (morph?.eyeSparkleScale ?? 1) * (lookMorph?.eyeSparkleScale ?? 1) * (light?.eyeSparkleScale ?? 1) * (skinTone?.eyeSparkleScale ?? 1),
        lipColor: hexToRgbTuple(r.hex),
        lipOpacity: r.opacity * preset.lipOpacityScale * (tuning?.lipOpacityScale ?? 1) * (morph?.lipOpacityScale ?? 1) * (lookMorph?.lipOpacityScale ?? 1) * (light?.lipOpacityScale ?? 1) * (skinTone?.lipOpacityScale ?? 1),
        lipFinish: finishToNumber(r.finish),
        browColor: [92, 58, 46],
        browOpacity: preset.eyebrowOpacity,
        linerColor: selectedStyle.kit?.eyeshadow ? [0, 0, 0] : undefined,
        linerOpacity: selectedStyle.kit?.eyeshadow ? 0.75 : 0,
        linerThickness: 0.5,
        hairColor: selectedHairShade ? hexToRgbTuple(selectedHairShade.hex) : undefined,
        hairStrength: 0.85,
        sharpenStrength: 0.28,
        vibrance: 0.18
      });
    } catch (err) {
      webglRendering = false;
    }
  }

  let isComposing = false;
  function renderCurrent() {
    if (isComposing) return;
    if (!previewEl) return;
    if (premiumRenderDataUrl && !compareMode) {
      const img = new Image();
      img.src = premiumRenderDataUrl;
      img.alt = selectedStyle ? copy.premium.alt(selectedStyle.name) : copy.premium.genericAlt;
      img.style.width = '100%';
      img.style.height = 'auto';
      img.style.display = 'block';
      img.setAttribute('data-vto-premium-render', premiumRenderMode || 'server');
      previewEl.replaceChildren(img);
      return;
    }
    if (afterPreviewCanvas && !showOriginal && !compareMode) {
      previewEl.replaceChildren(afterPreviewCanvas);
      return;
    }
    isComposing = true;
    let canvas: HTMLCanvasElement | null = null;
    try {
      canvas = composeLookCanvas();
      renderFallbackNotice = false;
    } catch (err) {
      canvas = composeEmergencyLookCanvas();
      renderFallbackNotice = Boolean(canvas);
      track('vto_render_fallback_used', {
        reason: (err as Error).message || 'render_exception'
      });
    } finally {
      isComposing = false;
    }
    if (!canvas) return;
    canvas.style.width = '100%';
    canvas.style.height = 'auto';
    canvas.style.display = 'block';
    if (compareMode) canvas.setAttribute('data-after', '');
    if (!showOriginal && !compareMode) afterPreviewCanvas = canvas;
    previewEl.replaceChildren(canvas);

    // The Canvas2D composition above (composeLookCanvas) serves as the immediate
    // live preview in every mode. WebGL2 kicks off asynchronously and swaps in
    // when ready on the "after" view. Bugs 1-3 (Y-flip, uniform names, skinMean)
    // were already fixed in the shader source; bug 4 (hard cheek mask) is now
    // resolved via cheek mask softening in TexturePacker.packMaskAtlases.
    // RE-DISABLED 2026-06-09: WebGL2 swap-in introduced visual regressions
    // (hue shifts, blit corruption) on production. Restore after per-device
    // visual QA on the staging branch.
    //
    // if (canUseWebGL2Renderer() && semanticFaceMap && !webglRendering && !showOriginal && !compareMode) {
    //   webglRendering = true;
    //   renderWebGLMakeup().finally(() => { webglRendering = false; });
    // }
  }

  function snapshotCanvas(kind = 'original'): HTMLCanvasElement | null {
    if (!snapshot) return null;
    const c = document.createElement('canvas');
    c.width = snapshot.width;
    c.height = snapshot.height;
    const ctx = c.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(snapshot.bitmap, 0, 0);
    c.setAttribute('data-vto-fallback', kind);
    return c;
  }

  function composeEmergencyLookCanvas(): HTMLCanvasElement | null {
    const original = snapshotCanvas('emergency-original');
    if (!original || !detection || !selectedStyle) return original;
    try {
      const r = resolveRenderable(selectedStyle, null);
      const rendered = renderLipOverlay(original, detection, {
        ...r,
        opacity: Math.min(0.72, r.opacity * currentPreset().lipOpacityScale),
        semanticMap: null
      }).canvas;
      rendered.setAttribute('data-vto-fallback', 'emergency-lip');
      return rendered;
    } catch {
      return original;
    }
  }

  function renderBefore() {
    if (!snapshot || !beforeEl) return;
    const c = snapshotCanvas('before');
    if (!c) return;
    c.style.width = '100%';
    c.style.height = 'auto';
    c.style.display = 'block';
    c.setAttribute('data-before', '');
    beforeEl.replaceChildren(c);
  }

  function renderOriginal() {
    if (!snapshot || !previewEl) return;
    const c = snapshotCanvas('original');
    if (!c) return;
    c.style.width = '100%';
    c.style.height = 'auto';
    c.style.display = 'block';
    previewEl.replaceChildren(c);
  }

  function composeLookCanvas(): HTMLCanvasElement | null {
    if (!snapshot || !detection || !selectedStyle) return null;
    const r = resolveRenderable(selectedStyle, null);
    const tuning = quality?.tuning;
    const morph = morphology?.tuning;
    const lookMorph = lookMorphology?.tuning;
    const st = subtone;
    const light = lightingCalibration?.tuning;
    const skinTone = skinToneCalibration?.tuning;
    const preset = currentPreset();
    const skinWorkMax = semanticMode === 'balanced' ? SKIN_WORK_MAX_BALANCED : SKIN_WORK_MAX_HIGH;
    const flowTiming = capturePerformanceTiming ?? emptyVtoPerformanceTiming();
    const timing = {
      ...flowTiming,
      skinMs: 0,
      complexionMs: 0,
      cheekMs: 0,
      eyeMs: 0,
      lipMs: 0,
      browMs: 0,
      refinementMs: 0,
      renderMs: 0
    };
    const renderStart = performance.now();
    let stageStart = renderStart;

    // 0. Skin smoothing
    let base: HTMLCanvasElement | ImageBitmap;
    try {
      base = renderSkinFrequencySeparation(snapshot.bitmap, detection, {
        strength: Math.min(
          preset.skinStrengthCap,
          (tuning?.skinStrength ?? 0.4) * (light?.skinStrengthScale ?? 1)
            * (skinTone?.skinStrengthScale ?? 1)
        ),
        semanticMap: semanticFaceMap,
        workMax: skinWorkMax
      }).canvas;
    } catch {
      base = renderSkinSmoothing(snapshot.bitmap, detection, {
        opacity: Math.min(
          preset.skinFallbackOpacityCap,
          (tuning?.skinStrength ?? 0.3) * (light?.skinStrengthScale ?? 1)
            * (skinTone?.skinStrengthScale ?? 1)
        ),
        semanticMap: semanticFaceMap
      }).canvas;
    }
    timing.skinMs = roundedMs(performance.now() - stageStart);

    // 0b. Complexion veil + under-eye corrector. Kept deliberately light:
    // this is a natural base correction, not a heavy foundation shade swap.
    stageStart = performance.now();
    let prevComposite: HTMLCanvasElement | ImageBitmap = base;
    try {
      base = renderComplexionOverlay(base, detection, {
        veilOpacity: 0.075
          * preset.complexionOpacityScale
          * (light?.skinStrengthScale ?? 1)
          * (skinTone?.skinStrengthScale ?? 1),
        underEyeOpacity: 0.11
          * preset.underEyeOpacityScale
          * (light?.skinStrengthScale ?? 1)
          * (skinTone?.skinStrengthScale ?? 1),
        semanticMap: semanticFaceMap
      }).canvas;
    } catch {
      // Complexion is an enhancement layer; if it fails, continue with the
      // already-rendered skin base so the VTO still produces a result.
    }
    if (base !== prevComposite) releaseCanvas(prevComposite);
    timing.complexionMs = roundedMs(performance.now() - stageStart);

    // 0c. Hair recolour. Independent of the makeup look; needs the ONNX hair
    // mask (real selfies / high semantic mode). Reuses semanticMap.masks.hair
    // + a 'color'-blend tint (see hairOverlay) — additive, never blocking.
    if (selectedHairShade && semanticFaceMap?.masks?.hair) {
      prevComposite = base;
      try {
        base = renderHairOverlay(base, {
        hex: selectedHairShade.hex,
        semanticMap: semanticFaceMap,
        personAlpha: hairPersonAlpha ?? undefined
      }).canvas;
      } catch {
        // Hair recolour is additive; never break the makeup pipeline.
      }
      if (base !== prevComposite) releaseCanvas(prevComposite);
    }

    // 1. Blush with subtone-aware warmth shift
    if (selectedStyle.kit?.blush) {
      stageStart = performance.now();
      prevComposite = base;
      base = renderCheekOverlay(base, detection, {
        hex: selectedStyle.kit.blush.hex,
        finish: selectedStyle.kit.blush.finish,
        opacityScale: preset.cheekOpacityScale
          * (tuning?.cheekOpacityScale ?? 1)
          * (morph?.cheekOpacityScale ?? 1)
          * (lookMorph?.cheekOpacityScale ?? 1)
          * (light?.cheekOpacityScale ?? 1)
          * (skinTone?.cheekOpacityScale ?? 1)
          * (st ? 1 + st.blushWarmthShift : 1),
        radiusScale: preset.cheekRadiusScale
          * (tuning?.cheekRadiusScale ?? 1)
          * (morph?.cheekRadiusScale ?? 1)
          * (lookMorph?.cheekRadiusScale ?? 1),
        drapeScale: preset.cheekDrapeScale
          * (tuning?.cheekDrapeScale ?? 1)
          * (morph?.cheekDrapeScale ?? 1)
          * (lookMorph?.cheekDrapeScale ?? 1),
        semanticMap: semanticFaceMap
      }).canvas;
      if (base !== prevComposite) releaseCanvas(prevComposite);
      timing.cheekMs = roundedMs(performance.now() - stageStart);
    }

    // 2. Eyeshadow with subtone-aware opacity and vertical gradient
    if (selectedStyle.kit?.eyeshadow) {
      stageStart = performance.now();
      prevComposite = base;
      base = renderEyeOverlay(base, detection, {
        hex: selectedStyle.kit.eyeshadow.hex,
        finish: selectedStyle.kit.eyeshadow.finish,
        opacityScale: preset.eyeOpacityScale
          * (tuning?.eyeOpacityScale ?? 1)
          * (morph?.eyeOpacityScale ?? 1)
          * (lookMorph?.eyeOpacityScale ?? 1)
          * (light?.eyeOpacityScale ?? 1)
          * (skinTone?.eyeOpacityScale ?? 1)
          * (st?.eyeOpacityGain ?? 1),
        shapeTightness: Math.max(
          preset.eyeShapeTightness,
          tuning?.eyeShapeTightness ?? 0,
          morph?.eyeShapeTightness ?? 0,
          lookMorph?.eyeShapeTightness ?? 0
        ),
        featherScale: preset.eyeFeatherScale
          * (tuning?.eyeFeatherScale ?? 1)
          * (morph?.eyeFeatherScale ?? 1)
          * (lookMorph?.eyeFeatherScale ?? 1),
        sparkleScale: preset.eyeSparkleScale
          * (tuning?.eyeSparkleScale ?? 1)
          * (morph?.eyeSparkleScale ?? 1)
          * (lookMorph?.eyeSparkleScale ?? 1)
          * (light?.eyeSparkleScale ?? 1)
          * (skinTone?.eyeSparkleScale ?? 1),
        semanticMap: semanticFaceMap
      }).canvas;
      if (base !== prevComposite) releaseCanvas(prevComposite);
      timing.eyeMs = roundedMs(performance.now() - stageStart);
    }

    // 3. Lipstick
    stageStart = performance.now();
    prevComposite = base;
    base = renderLipOverlay(base, detection, {
      ...r,
      opacity: r.opacity
        * preset.lipOpacityScale
        * (tuning?.lipOpacityScale ?? 1)
        * (morph?.lipOpacityScale ?? 1)
        * (lookMorph?.lipOpacityScale ?? 1)
        * (light?.lipOpacityScale ?? 1)
        * (skinTone?.lipOpacityScale ?? 1),
      innerHoleScale: (tuning?.lipInnerHoleScale ?? 1)
        * (morph?.lipInnerHoleScale ?? 1)
        * (lookMorph?.lipInnerHoleScale ?? 1),
      finishStrengthScale: preset.lipFinishScale
        * (tuning?.lipFinishScale ?? 1)
        * (morph?.lipFinishScale ?? 1)
        * (lookMorph?.lipFinishScale ?? 1)
        * (light?.lipFinishScale ?? 1)
        * (skinTone?.lipFinishScale ?? 1),
      semanticMap: semanticFaceMap
    }).canvas;
    if (base !== prevComposite) releaseCanvas(prevComposite);
    timing.lipMs = roundedMs(performance.now() - stageStart);

    // 4. Eyebrow darkening (on top of everything)
    stageStart = performance.now();
    const finalCanvas = renderEyebrowOverlay(base, detection, {
      opacity: preset.eyebrowOpacity
    }).canvas;
    if (finalCanvas !== base) releaseCanvas(base);
    timing.browMs = roundedMs(performance.now() - stageStart);

    stageStart = performance.now();
    const refined = refineMakeupComposite(snapshot.bitmap, finalCanvas, detection, {
      semanticMap: semanticFaceMap
    }).canvas;
    if (refined !== finalCanvas) releaseCanvas(finalCanvas);
    timing.refinementMs = roundedMs(performance.now() - stageStart);
    timing.renderMs = roundedMs(performance.now() - renderStart);
    timing.totalMs = roundedMs(
      timing.detectionMs +
      timing.qualityMs +
      timing.semanticMs +
      timing.calibrationMs +
      timing.renderMs
    );
    capturePerformanceTiming = timing;
    performanceTiming = timing;
    applyVtoTimingAttributes(refined, timing);
    lastRefinementAttributes = {};
    ['data-refinement', 'data-refinement-runtime', 'data-refinement-platform'].forEach((key) => {
      const value = refined.getAttribute(key);
      if (value) lastRefinementAttributes[key] = value;
    });
    return refined;
  }

  // Rendering is now triggered explicitly after state changes that require
  // a new composite (style, intensity, preview mode). This avoids infinite
  // $effect loops caused by DOM mutations inside renderCurrent/showWebGLBitmap.

  function retakePhoto() {
    clearPremiumRender();
    bridge?.destroy();
    bridge = null;
    webglBitmap = null;
    webglResultCanvas = null;
    afterPreviewCanvas = null;
    lastRefinementAttributes = {};
    replaceSnapshot(null);
    detection = null;
    quality = null;
    morphology = null;
    lookMorphology = null;
    subtone = null;
    lightingCalibration = null;
    skinToneCalibration = null;
    semanticFaceMap = null;
    hairPersonAlpha = null;
    detectionCacheKey = null;
    capturePerformanceTiming = null;
    performanceTiming = null;
    showOriginal = false;
    compareMode = false;
    phase = 'capturing';
  }

  function backToGallery() {
    clearPremiumRender();
    quality = null;
    morphology = null;
    lookMorphology = null;
    subtone = null;
    lightingCalibration = null;
    skinToneCalibration = null;
    semanticFaceMap = null;
    hairPersonAlpha = null;
    detectionCacheKey = null;
    capturePerformanceTiming = null;
    performanceTiming = null;
    lastRefinementAttributes = {};
    compareMode = false;
    phase = 'landing';
  }

  function onMirrorCapture(result: CaptureResult): void {
    phase = 'processing';
    processingStage = 'detecting';
    void handleCaptured(result, 'high');
  }

  async function loadDemoPhoto(age: string): Promise<void> {
    if (!canStartTryFlow()) return;
    phase = 'processing';
    processingStage = 'detecting';
    try {
      const result = await captureFromUrl(`${import.meta.env.BASE_URL}${age}.png`);
      await handleCaptured(result, 'balanced');
    } catch (err) {
      detectionError = (err as Error).message || copy.loadDemoError;
      phase = 'no-face';
    }
  }

  async function onShareClick() {
    if (!snapshot || !detection || !selectedStyle) return;
    sharing = true;
    track('share_clicked', { style: selectedStyle.id });
    try {
      let canvas: HTMLCanvasElement | null = null;
      // Prefer WebGL2 result if available (P3, sharpened, vibrance)
      if (webglResultCanvas) {
        canvas = webglResultCanvas;
      } else {
        canvas = composeLookCanvas();
      }
      if (!canvas) return;
      const displayStyle = localizeStyleForDisplay(selectedStyle, lang);
      const watermarked = applyWatermark(canvas, displayStyle, null);
      const blob = await canvasToBlob(watermarked);
      const outcome = await shareComposite({ blob, style: displayStyle, activeMatch: null });
      track('share_completed', { style: selectedStyle.id, outcome });
    } finally {
      sharing = false;
    }
  }

  function canvasToScaledJpegDataUrl(
    canvas: HTMLCanvasElement,
    maxSide = 1600,
    quality = 0.9
  ): string {
    const longest = Math.max(canvas.width, canvas.height);
    if (longest <= maxSide) {
      return canvas.toDataURL('image/jpeg', quality);
    }

    const scale = maxSide / longest;
    const out = document.createElement('canvas');
    out.width = Math.max(1, Math.round(canvas.width * scale));
    out.height = Math.max(1, Math.round(canvas.height * scale));
    const ctx = out.getContext('2d', { alpha: false });
    if (!ctx) return canvas.toDataURL('image/jpeg', quality);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(canvas, 0, 0, out.width, out.height);
    return out.toDataURL('image/jpeg', quality);
  }

  async function applyPremiumRenderResponse(data: any): Promise<void> {
    premiumRenderMode = data.mode || 'client_fallback';
    premiumRenderJobId = typeof data.job_id === 'string' ? data.job_id : premiumRenderJobId;
    if (typeof data.image_jpeg === 'string' && data.image_jpeg.startsWith('data:image/jpeg;base64,')) {
      premiumRenderDataUrl = data.image_jpeg;
      showOriginal = false;
      compareMode = false;
      await nextPaint();
      renderCurrent();
      premiumRenderStatus = data.runtime_ms
        ? copy.premium.readyMs(data.runtime_ms)
        : copy.premium.ready;
    } else {
      premiumRenderStatus = copy.premium.localReady;
    }
    track('premium_render_completed', {
      style: selectedStyle?.id ?? '',
      mode: premiumRenderMode,
      status: data.status || 'completed'
    });
  }

  async function pollPremiumRenderJob(jobId: string, trialId: string): Promise<void> {
    const maxAttempts = 18;
    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      await sleep(attempt === 0 ? 700 : 1300);
      const resp = await fetch(
        `/api/vto_render_status.php?job_id=${encodeURIComponent(jobId)}&trial_id=${encodeURIComponent(trialId)}`,
        { cache: 'no-store' }
      );
      const data = await resp.json();
      if (!resp.ok || !data.success) {
        throw new Error(data.error || `premium_status:${resp.status}`);
      }
      if (data.status === 'completed') {
        await applyPremiumRenderResponse(data);
        return;
      }
      if (data.status === 'failed') {
        throw new Error(data.error || 'premium_job_failed');
      }
      premiumRenderStatus = data.message || copy.premium.progress[premiumRenderProgressIndex] || copy.premium.queued;
    }
    throw new Error('premium_job_timeout');
  }

  async function requestPremiumRender(): Promise<void> {
    if (!PREMIUM_RENDER_ENABLED) return; // off for launch: keep VTO fully on-device
    if (!snapshot || !detection || !selectedStyle || premiumRendering) return;
    premiumRendering = true;
    startPremiumRenderProgress();
    premiumRenderError = '';
    track('premium_render_started', { style: selectedStyle.id });

    try {
      const canvas = composeLookCanvas();
      if (!canvas) throw new Error('compose_failed');
      const access = refreshAccessState();
      const csrfToken = await fetchCsrfToken();
      const resp = await fetch('/api/vto_render.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          csrf_token: csrfToken,
          delivery: 'job',
          trial_id: access.visitorId,
          style_id: selectedStyle.id,
          preview_jpeg: canvasToScaledJpegDataUrl(canvas),
          context: {
            intensity: makeupIntensity,
            preset: premiumPresetForIntensity(makeupIntensity),
            semantic_mode: semanticMode,
            lighting: lightingCalibration
              ? {
                  exposure: lightingCalibration.exposure,
                  contrast: lightingCalibration.contrast,
                  white_balance: lightingCalibration.whiteBalance
                }
              : null,
            skin: skinToneCalibration
              ? {
                  depth: skinToneCalibration.depth,
                  chroma: skinToneCalibration.chroma,
                  ita: Math.round(skinToneCalibration.ita)
                }
              : null
          }
        })
      });
      const data = await resp.json();
      if (!resp.ok || !data.success) {
        throw new Error(data.error || `premium_render:${resp.status}`);
      }

      premiumRenderJobId = typeof data.job_id === 'string' ? data.job_id : '';
      if ((data.status === 'queued' || data.queued === true) && premiumRenderJobId) {
        premiumRenderStatus = data.message || copy.premium.queued;
        await pollPremiumRenderJob(premiumRenderJobId, access.visitorId);
      } else {
        await applyPremiumRenderResponse(data);
      }
    } catch (err) {
      premiumRenderError = copy.premium.unavailable;
      premiumRenderStatus = '';
      track('premium_render_failed', {
        style: selectedStyle.id,
        reason: (err as Error).message || 'unknown'
      });
    } finally {
      stopPremiumRenderProgress();
      premiumRendering = false;
    }
  }

  function fillRoundedRect(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    w: number,
    h: number,
    r: number
  ) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
    ctx.fill();
  }

  function drawWrappedText(
    ctx: CanvasRenderingContext2D,
    text: string,
    x: number,
    y: number,
    maxWidth: number,
    lineHeight: number,
    maxLines = 4
  ): number {
    const words = text.split(/\s+/).filter(Boolean);
    const lines: string[] = [];
    let current = '';
    for (const word of words) {
      const next = current ? `${current} ${word}` : word;
      if (ctx.measureText(next).width <= maxWidth || !current) {
        current = next;
      } else {
        lines.push(current);
        current = word;
        if (lines.length >= maxLines) break;
      }
    }
    if (current && lines.length < maxLines) lines.push(current);
    lines.forEach((line, index) => ctx.fillText(line, x, y + index * lineHeight));
    return y + lines.length * lineHeight;
  }

  function drawSwatch(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    label: string,
    finish: string,
    color: string
  ) {
    ctx.fillStyle = color;
    fillRoundedRect(ctx, x, y, 104, 104, 52);
    ctx.strokeStyle = 'rgba(28, 26, 23, 0.12)';
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.fillStyle = LOOK_CARD_INK;
    ctx.font = '700 24px Inter, system-ui, sans-serif';
    ctx.fillText(label, x, y + 146);
    ctx.fillStyle = LOOK_CARD_MUTED;
    ctx.font = '400 22px Inter, system-ui, sans-serif';
    drawWrappedText(ctx, finish, x, y + 178, 210, 27, 2);
  }

  function renderLookCardCanvas(style: MakeupStyle): HTMLCanvasElement {
    const canvas = document.createElement('canvas');
    canvas.width = 1080;
    canvas.height = 1350;
    const ctx = canvas.getContext('2d');
    if (!ctx) return canvas;
    const map = style.cosmeticMap;

    ctx.fillStyle = LOOK_CARD_BG;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = LOOK_CARD_SURFACE;
    fillRoundedRect(ctx, 58, 58, 964, 1234, 42);

    ctx.fillStyle = style.hex;
    fillRoundedRect(ctx, 88, 88, 904, 18, 9);

    ctx.fillStyle = LOOK_CARD_ACCENT;
    ctx.font = '700 28px Inter, system-ui, sans-serif';
    ctx.fillText('BEAUTYIQ LOOK CARD', 88, 168);

    ctx.fillStyle = LOOK_CARD_INK;
    ctx.font = '700 78px Playfair Display, Georgia, serif';
    drawWrappedText(ctx, style.name, 88, 250, 760, 86, 2);

    ctx.fillStyle = LOOK_CARD_MUTED;
    ctx.font = '400 30px Inter, system-ui, sans-serif';
    let y = drawWrappedText(ctx, map?.intent ?? style.tagline, 88, 368, 820, 40, 3) + 42;

    ctx.fillStyle = LOOK_CARD_BG;
    fillRoundedRect(ctx, 88, y, 904, 210, 28);
    drawSwatch(ctx, 128, y + 34, copy.kit.lipstick, style.finish, style.hex);
    if (style.kit) {
      drawSwatch(ctx, 420, y + 34, copy.kit.blush, style.kit.blush.finish, style.kit.blush.hex);
      drawSwatch(ctx, 712, y + 34, copy.kit.eyeshadow, style.kit.eyeshadow.finish, style.kit.eyeshadow.hex);
    }
    y += 262;

    if (map) {
      const chips = [
        `${copy.lookCard.palette}: ${map.paletteFamily}`,
        `${copy.lookCard.temperature}: ${map.temperature}`,
        `${copy.lookCard.contrast}: ${map.contrast}`,
        `${copy.lookCard.chroma}: ${map.chroma}`,
        map.skinDepthFit
      ];
      ctx.font = '700 23px Inter, system-ui, sans-serif';
      let chipX = 88;
      let chipY = y;
      for (const chip of chips) {
        const width = Math.min(860, ctx.measureText(chip).width + 44);
        if (chipX + width > 992) {
          chipX = 88;
          chipY += 58;
        }
        ctx.fillStyle = '#ffd36f';
        fillRoundedRect(ctx, chipX, chipY, width, 42, 21);
        ctx.fillStyle = LOOK_CARD_ACCENT;
        ctx.fillText(chip, chipX + 22, chipY + 29);
        chipX += width + 14;
      }
      y = chipY + 92;

      ctx.fillStyle = LOOK_CARD_ACCENT;
      ctx.font = '700 26px Inter, system-ui, sans-serif';
      ctx.fillText(copy.lookCard.counterBrief, 88, y);
      y += 44;
      ctx.fillStyle = LOOK_CARD_INK;
      ctx.font = '400 27px Inter, system-ui, sans-serif';
      y = drawWrappedText(ctx, `${copy.lookCard.eyes}: ${map.placement.eyes}`, 88, y, 860, 36, 2) + 18;
      y = drawWrappedText(ctx, `${copy.lookCard.cheeks}: ${map.placement.cheeks}`, 88, y, 860, 36, 2) + 18;
      y = drawWrappedText(ctx, `${copy.lookCard.lips}: ${map.placement.lips}`, 88, y, 860, 36, 2) + 30;

      ctx.fillStyle = LOOK_CARD_MUTED;
      ctx.font = '400 24px Inter, system-ui, sans-serif';
      drawWrappedText(ctx, `${copy.lookCard.avoid}: ${map.counterBrief.join(', ')}`, 88, y, 860, 32, 3);
    }

    ctx.fillStyle = LOOK_CARD_ACCENT;
    ctx.font = '700 26px Inter, system-ui, sans-serif';
    ctx.fillText(`beautyiq.cloud/try-on/?style=${style.id}`, 88, 1236);
    ctx.fillStyle = LOOK_CARD_MUTED;
    ctx.font = '400 22px Inter, system-ui, sans-serif';
    ctx.fillText(copy.lookCard.canvasFooter, 88, 1278);

    return canvas;
  }

  async function onShareLookCardClick() {
    if (!selectedStyle) return;
    cardSharing = true;
    track('look_card_share_clicked', { style: selectedStyle.id });
    try {
      const displayStyle = localizeStyleForDisplay(selectedStyle, lang);
      const canvas = renderLookCardCanvas(displayStyle);
      const blob = await canvasToBlob(canvas, 0.92);
      const outcome = await shareComposite({ blob, style: displayStyle, activeMatch: null });
      track('look_card_share_completed', { style: selectedStyle.id, outcome });
    } finally {
      cardSharing = false;
    }
  }

  let dragging = false;
  let lastSliderHapticSide: 'left' | 'right' | null = null;

  function updateSliderFromPointer(e: PointerEvent) {
    if (!compareEl) return;
    const rect = compareEl.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const newPos = Math.max(2, Math.min(98, (x / rect.width) * 100));
    sliderPos = newPos;
    const side = newPos > 50 ? 'right' : 'left';
    if (side !== lastSliderHapticSide) {
      hapticLight();
      lastSliderHapticSide = side;
    }
  }

  function startSliderDrag(e: PointerEvent) {
    dragging = true;
    lastSliderHapticSide = null;
    updateSliderFromPointer(e);
    compareEl?.setPointerCapture(e.pointerId);
  }

  function dragSlider(e: PointerEvent) {
    if (!dragging) return;
    updateSliderFromPointer(e);
  }

  function stopSliderDrag(e: PointerEvent) {
    dragging = false;
    try { compareEl?.releasePointerCapture(e.pointerId); } catch { /* ok */ }
  }

  function onSliderKeydown(e: KeyboardEvent) {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight' && e.key !== 'Home' && e.key !== 'End') {
      return;
    }
    e.preventDefault();
    if (e.key === 'Home') sliderPos = 2;
    else if (e.key === 'End') sliderPos = 98;
    else sliderPos = Math.max(2, Math.min(98, sliderPos + (e.key === 'ArrowRight' ? 4 : -4)));
  }

</script>

{#if import.meta.env.DEV}
  <!-- This badge is stripped at build time and will never appear in production -->
  <div
    class="fixed top-2 right-2 z-50 rounded-full bg-red-600 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white shadow-lg pointer-events-none"
  >
    Dev - Unlimited VTO
  </div>
{/if}

<main class="mx-auto flex min-h-[100dvh] max-w-md flex-col gap-6 px-5 pt-8 pb-[calc(2rem+env(safe-area-inset-bottom))]">
  <header class="text-center">
    <p class="text-xs uppercase tracking-widest text-brand-muted">{copy.common.brand}</p>
    <h1 class="mt-1 font-display text-3xl text-brand-accent">{copy.common.vtoTitle}</h1>
    {#if phase === 'landing'}
      <p class="mt-2 text-sm text-brand-muted">
        {copy.onboarding.subtitle}
      </p>
    {:else if phase === 'capturing'}
      <p class="mt-2 text-sm text-brand-muted">
        {copy.captureHeader.subtitle}
      </p>
    {/if}
  </header>

  {#if phase === 'landing'}
    {@const introStyle = onboardingStyle()}
    <section class="card space-y-5">
      <div class="space-y-3">
        <div class="flex items-start justify-between gap-4">
          <div class="min-w-0">
            <p class="text-[11px] font-semibold uppercase tracking-wider text-brand-accent">
              {copy.onboarding.eyebrow}
            </p>
            <h2 class="mt-1 font-display text-2xl leading-tight text-brand-ink">
              {copy.onboarding.title}
            </h2>
          </div>
          {#if introStyle}
            <span
              class="block h-14 w-14 shrink-0 rounded-full border border-black/10 shadow-sm"
              style="background: radial-gradient(circle at 32% 28%, {introStyle.hex}ee, {introStyle.hex} 68%, {introStyle.hex}aa 100%);"
              aria-hidden="true"
            ></span>
          {/if}
        </div>

        <p class="text-[13px] leading-relaxed text-brand-muted">
          {copy.onboarding.body}
        </p>
      </div>

      {#if introStyle}
        {@const introStyleDisplay = localizeStyleForDisplay(introStyle, lang)}
        <div class="rounded-md bg-brand-bg px-3 py-3">
          <p class="text-[10px] font-semibold uppercase tracking-wider text-brand-accent">
            {selectedStyle ? copy.onboarding.selectedLook : copy.onboarding.initialLook}
          </p>
          <p class="mt-1 text-sm font-semibold text-brand-ink">{introStyleDisplay.name}</p>
          <p class="mt-0.5 text-[12px] leading-relaxed text-brand-muted">{introStyleDisplay.tagline}</p>
        </div>
      {/if}

      <div class="space-y-2">
        <button
          class="btn-primary w-full"
          onclick={() => {
            if (canStartTryFlow()) {
              // Devices with a camera (iPhone included — the mirror only needs
              // camera + MediaPipe + Canvas2D, see liveCapability.ts) open the
              // live mirror; camera-less devices fall back to photo capture.
              phase = canUseLiveMirror() ? 'mirror' : 'capturing';
            }
          }}
        >
          {copy.onboarding.cta}
        </button>
        <p class="text-center text-[11px] leading-relaxed text-brand-muted">
          {copy.onboarding.privacy}
        </p>
      </div>

      <div class="grid gap-2">
        {#each copy.onboarding.checks as check, index (check.label)}
          <div class="grid grid-cols-[2rem_1fr] gap-3 rounded-md border border-brand-highlight bg-white px-3 py-2.5">
            <span class="flex h-8 w-8 items-center justify-center rounded-full bg-brand-highlight text-xs font-bold text-brand-accent">
              {index + 1}
            </span>
            <div>
              <p class="text-[12px] font-semibold text-brand-ink">{check.label}</p>
              <p class="mt-0.5 text-[11px] leading-relaxed text-brand-muted">{check.detail}</p>
            </div>
          </div>
        {/each}
      </div>
    </section>

    <!-- Built-in demo photos (20/30/40/50-year-old reference portraits
         bundled in vto-pwa/public/, served at <BASE>/20.png .. <BASE>/50.png).
         Dual purpose: the user can preview the VTO without a selfie, and we
         keep a consistent test set for tuning renderer parameters across
         ages/face shapes. -->
    <section class="rounded-card border border-brand-accent/15 bg-brand-surface px-4 py-4 shadow-sm">
      <div class="flex items-end justify-between gap-3">
        <div>
          <p class="text-[11px] font-semibold uppercase tracking-wider text-brand-accent">
            {copy.onboarding.demoTitle}
          </p>
          <p class="mt-1 text-[12px] leading-relaxed text-brand-muted">
            {copy.onboarding.demoBody}
          </p>
        </div>
        <span class="shrink-0 text-[11px] font-semibold text-brand-muted">{copy.common.demo}</span>
      </div>
      <div class="mt-3 grid grid-cols-4 gap-2 w-full">
        {#each ['20', '30', '40', '50'] as age (age)}
          <button
            class="min-h-[44px] rounded-pill border border-brand-accent/30 bg-white py-2 text-xs font-semibold text-brand-accent shadow-sm"
            onclick={() => loadDemoPhoto(age)}
          >
            {age}
          </button>
        {/each}
      </div>
    </section>
  {:else if phase === 'capturing'}
    <!-- Rules already shown on the intro phase; here just the camera. -->
    <Capture oncapture={handleCaptured} copy={copy.capture} />
    <button
      class="inline-flex min-h-[44px] w-full items-center justify-center text-sm text-brand-muted underline underline-offset-2"
      onclick={backToGallery}
    >
      {copy.common.cancel}
    </button>

  {:else if phase === 'mirror'}
    <LiveMirror {copy} crossBrand={crossBrandTeaser} oncapture={onMirrorCapture} onusephoto={() => { phase = 'capturing'; }} />

  {:else if phase === 'processing'}
    {@const currentProcessing = processingCopy(processingStage)}
    <section class="card w-full space-y-3 text-center" aria-live="polite">
      <div class="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-brand-accent/20 border-t-brand-accent"></div>
      <div class="space-y-1">
        <p class="font-semibold text-brand-ink">
          {currentProcessing.title}
        </p>
        <p class="text-xs text-brand-muted">
          {currentProcessing.detail}
        </p>
      </div>
      <div class="h-1.5 overflow-hidden rounded-full bg-brand-bg">
        <div
          class="h-full rounded-full bg-brand-accent transition-all duration-300"
          style="width: {currentProcessing.progress}%;"
        ></div>
      </div>
      <p class="text-[11px] leading-relaxed text-brand-muted">
        {copy.processing.waitHint}
      </p>
    </section>

  {:else if phase === 'paywall'}
    <section class="card w-full space-y-4 text-center" data-testid="vto-paywall">
      <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-brand-highlight text-xl">
        🔒
      </div>
      <div class="space-y-2">
        <p class="font-display text-2xl text-brand-accent">
          {copy.paywall.title(FREE_TRY_LIMIT)}
        </p>
        <p class="text-sm leading-relaxed text-brand-muted">
          {copy.paywall.body}
        </p>
        {#if paywallReason}
          <p class="sr-only">Motivo paywall: {paywallReason}</p>
        {/if}
      </div>
      <button class="btn-primary w-full" type="button" disabled={checkoutLoading} onclick={startCheckout}>
        {checkoutLoading ? copy.paywall.loading : copy.paywall.cta}
      </button>
      {#if checkoutError}
        <p class="text-xs font-semibold text-red-700" aria-live="polite">{checkoutError}</p>
      {/if}
      <a
        class="inline-flex w-full items-center justify-center rounded-pill border border-brand-accent/30 px-4 py-3 text-sm font-semibold text-brand-accent no-underline"
        href="/index.html"
      >
        {copy.common.backToBeautyIQ}
      </a>
      <p class="text-[11px] leading-relaxed text-brand-muted">
        {copy.paywall.privacy}
      </p>
    </section>

  {:else if phase === 'no-face'}
    <section class="card w-full space-y-3 text-center">
      <p class="font-semibold">{copy.noFace.title}</p>
      <p class="text-sm text-brand-muted">
        {detectionError ||
          copy.noFace.fallback}
      </p>
      <button class="btn-primary w-full" onclick={retakePhoto}>{copy.common.retry}</button>
    </section>

  {:else if phase === 'result' && selectedStyle}
    {@const selectedStyleDisplay = localizeStyleForDisplay(selectedStyle, lang)}
    <!-- Face preview: compare-mode shows a draggable slider; default mode
         shows the composed look with a tap-to-compare toggle below. -->
    {#if compareMode}
      <div
        bind:this={compareEl}
        class="relative overflow-hidden rounded-card bg-black select-none"
        style="user-select: none; touch-action: none;"
        onpointerdown={startSliderDrag}
        onpointermove={dragSlider}
        onpointerup={stopSliderDrag}
        onpointercancel={stopSliderDrag}
        onpointerleave={stopSliderDrag}
        onkeydown={onSliderKeydown}
        oncontextmenu={(e) => e.preventDefault()}
        tabindex="0"
        role="slider"
        aria-label={copy.result.compareLabel}
        aria-valuenow={Math.round(sliderPos)}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <!-- Before image (full) -->
        <div bind:this={beforeEl} class="w-full"></div>
        <!-- After image (clipped from the left by slider) -->
        <div
          bind:this={previewEl}
          class="absolute inset-0 overflow-hidden"
          style="clip-path: inset(0 {100 - sliderPos}% 0 0);"
        ></div>
        <!-- Slider handle -->
        <div
          class="pointer-events-none absolute top-0 bottom-0 w-1 bg-white shadow-md"
          style="left: {sliderPos}%; transform: translateX(-50%);"
          aria-hidden="true"
        >
          <span class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex h-8 w-8 items-center justify-center rounded-full bg-white shadow-lg text-xs font-bold text-brand-ink">⇔</span>
        </div>
        {#if recomposing}
          <div
            class="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/35 px-4 text-center text-white backdrop-blur-[2px]"
            aria-hidden="true"
          >
            <div class="h-8 w-8 animate-spin rounded-full border-2 border-white/30 border-t-white"></div>
            <p class="text-xs font-semibold">{copy.result.refineLook}</p>
          </div>
        {/if}
      </div>
    {:else}
      <div class="relative">
        <div
          bind:this={previewEl}
          class="overflow-hidden rounded-card bg-black"
          style="user-select: none; touch-action: manipulation;"
          role="button"
          aria-label={copy.result.previewToggle}
          tabindex="0"
          onpointerup={handlePreviewTap}
          onkeydown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handlePreviewTap(); } }}
        ></div>
        {#if recomposing}
          <div
            class="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/35 px-4 text-center text-white backdrop-blur-[2px]"
            aria-hidden="true"
          >
            <div class="h-8 w-8 animate-spin rounded-full border-2 border-white/30 border-t-white"></div>
            <p class="text-xs font-semibold">{copy.result.refineLook}</p>
          </div>
        {/if}
      </div>
    {/if}

    <div class="space-y-3">
      <div class="grid grid-cols-3 gap-2 rounded-pill bg-brand-bg p-1" aria-label={copy.result.viewLabel}>
        <button
          type="button"
          class="min-h-[44px] rounded-pill px-3 py-2 text-xs font-semibold transition {segmentedButtonClass(previewMode() === 'after')}"
          aria-pressed={previewMode() === 'after'}
          onclick={() => setPreviewMode('after')}
        >
          {copy.result.after}
        </button>
        <button
          type="button"
          class="min-h-[44px] rounded-pill px-3 py-2 text-xs font-semibold transition {segmentedButtonClass(previewMode() === 'before')}"
          aria-pressed={previewMode() === 'before'}
          onclick={() => setPreviewMode('before')}
        >
          {copy.result.before}
        </button>
        <button
          type="button"
          class="min-h-[44px] rounded-pill px-3 py-2 text-xs font-semibold transition {segmentedButtonClass(previewMode() === 'compare')}"
          aria-pressed={previewMode() === 'compare'}
          onclick={() => setPreviewMode('compare')}
        >
          {copy.result.compare}
        </button>
      </div>

      <div>
        <p class="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-brand-accent">
          {copy.result.intensity}
        </p>
        <div class="grid grid-cols-3 gap-2 rounded-pill bg-brand-bg p-1">
          {#each INTENSITY_ORDER as option (option)}
            <button
              type="button"
              class="min-h-[44px] rounded-pill px-3 py-2 text-xs font-semibold transition disabled:opacity-60 {segmentedButtonClass(makeupIntensity === option)}"
              aria-pressed={makeupIntensity === option}
              disabled={recomposing}
              onclick={() => setMakeupIntensity(option)}
            >
              {copy.intensity[option]}
            </button>
          {/each}
        </div>
      </div>
    </div>

    <!-- Look identity -->
    <div class="text-center">
      <h2 class="font-display text-xl text-brand-accent">{selectedStyleDisplay.name}</h2>
      <p class="text-sm text-brand-muted">{selectedStyleDisplay.tagline}</p>
      {#if subtone && subtone.confidence > 0.15}
        <p class="mt-0.5 text-[11px] text-brand-muted">
          {copy.result.subtone}: <span class="font-medium text-brand-ink">
            {subtone.class === 'warm' ? copy.result.warm : subtone.class === 'cool' ? copy.result.cool : copy.result.neutral}
          </span>
          - {copy.result.opacityAdjusted}
        </p>
      {/if}
      {#if lightingCalibration && lightingCalibration.confidence > 0.2}
        <p class="mt-0.5 text-[11px] text-brand-muted">
          {copy.result.light}: <span class="font-medium text-brand-ink">
            {lightingCalibration.exposure}, {lightingCalibration.contrast}, {lightingCalibration.whiteBalance}
          </span>
          - {copy.result.lightAdjusted}
        </p>
      {/if}
      {#if skinToneCalibration && skinToneCalibration.confidence > 0.2}
        <p class="mt-0.5 text-[11px] text-brand-muted" data-testid="skin-calibration">
          {copy.result.skin}: <span class="font-medium text-brand-ink">
            {skinDepthLabel(skinToneCalibration.depth)}, {skinChromaLabel(skinToneCalibration.chroma)}
          </span>
          - ITA {Math.round(skinToneCalibration.ita)}, {copy.result.skinAdjusted}
        </p>
      {/if}
      {#if renderFallbackNotice}
        <p class="mt-1 text-[11px] text-brand-muted">
          {copy.result.fallbackNotice}
        </p>
      {/if}
    </div>

    <!-- Horizontal look strip: 13 small swatches, snap-scroll. Tap to
         re-render the face in that look. The active look gets a ring
         highlight and slight scale so it's findable on swipe.
         py-3 on the scroll container: per CSS spec, `overflow-x:auto`
         forces y-axis clipping too, so without vertical padding the
         active bollino's ring (h-16 + ring-2 + ring-offset-2) gets
         clipped at the top edge of the strip. -->
    <div class="-mx-5 overflow-x-auto py-3">
      <div class="flex gap-2 px-5" style="scroll-snap-type: x mandatory;">
        {#each styles as style (style.id)}
          {@const active = selectedStyle?.id === style.id}
          <button
            class="flex flex-shrink-0 flex-col items-center gap-1"
            style="scroll-snap-align: center;"
            onclick={() => pickStyle(style)}
            aria-pressed={active}
            aria-label={style.name}
          >
            <span
              class="block rounded-full transition-all"
              class:h-14={!active}
              class:w-14={!active}
              class:h-16={active}
              class:w-16={active}
              class:ring-2={active}
              class:ring-offset-2={active}
              style="background: radial-gradient(circle at 30% 30%, {style.hex}ee, {style.hex} 70%, {style.hex}aa 100%); {active ? `--tw-ring-color: ${style.hex};` : 'opacity: 0.85;'}"
            ></span>
            <span class="max-w-[68px] truncate text-[10px] leading-tight {active ? 'font-semibold text-brand-ink' : 'text-brand-muted'}">
              {style.name}
            </span>
          </button>
        {/each}
      </div>
    </div>

    <!-- Hair colour (independent of the makeup look). Recolours the ONNX hair
         mask; subtone-flattering shades come first and are badged with ★. -->
    <div>
      <p class="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-brand-accent">
        {copy.result.hair}
      </p>
      <div class="flex gap-2 overflow-x-auto pb-1">
        <button
          type="button"
          class="min-h-[44px] flex-none rounded-pill px-3 text-xs font-semibold {segmentedButtonClass(!selectedHairShade)}"
          aria-pressed={!selectedHairShade}
          disabled={recomposing}
          onclick={() => pickHairShade(null)}
        >{copy.result.hairNone}</button>
        {#each sortedHairShades as shade (shade.id)}
          <button
            type="button"
            title={shade.name}
            aria-label={shade.name}
            aria-pressed={selectedHairShade?.id === shade.id}
            disabled={recomposing}
            class="relative h-11 w-11 flex-none rounded-full border-2 transition {selectedHairShade?.id === shade.id ? 'border-brand-accent' : 'border-black/10'}"
            style="background-color: {shade.hex}"
            onclick={() => pickHairShade(shade)}
          >
            {#if recommendedHairIds.has(shade.id)}
              <span class="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-brand-accent text-[9px] leading-none text-white">★</span>
            {/if}
          </button>
        {/each}
      </div>
      {#if selectedHairShade}
        <p class="mt-1 text-[11px] text-brand-muted">{selectedHairShade.name}{#if hairShadeNeedsLift(selectedHairShade)} · {copy.result.hairNote}{:else if hairShadeIsDark(selectedHairShade)} · {copy.result.hairNoteDark}{/if}</p>
      {:else if recommendedHairIds.size}
        <p class="mt-1 text-[11px] text-brand-muted">★ {copy.result.hairForYou}</p>
      {/if}
    </div>

    {#if performanceTiming}
      <section
        class="rounded-card border border-brand-accent/15 bg-brand-surface px-4 py-3 shadow-sm"
        data-testid="vto-quality-summary"
      >
        <div class="grid grid-cols-3 gap-2 text-center">
          <div>
            <p class="text-[10px] font-semibold uppercase tracking-wider text-brand-accent">{copy.result.quality}</p>
            <p class="mt-1 text-xs font-semibold text-brand-ink">{qualityStatusLabel()}</p>
          </div>
          <div>
            <p class="text-[10px] font-semibold uppercase tracking-wider text-brand-accent">{copy.result.engine}</p>
            <p class="mt-1 text-xs font-semibold text-brand-ink">{semanticModeLabel()}</p>
          </div>
          <div>
            <p class="text-[10px] font-semibold uppercase tracking-wider text-brand-accent">{copy.result.time}</p>
            <p class="mt-1 text-xs font-semibold text-brand-ink">{Math.max(1, Math.round(performanceTiming.totalMs / 1000))}s</p>
          </div>
        </div>
        <p class="mt-2 text-center text-[11px] leading-relaxed text-brand-muted">
          {copy.result.privacySummary(localEngineLabel())}
        </p>
      </section>
    {/if}

    <p class="text-center text-[11px] italic leading-relaxed text-brand-muted">
      {copy.result.simulationNote}
    </p>

    {#if debugMasks && semanticFaceMap}
      <SemanticDebugPanel
        map={semanticFaceMap}
        morphology={morphology}
        lookMorphology={lookMorphology}
        timing={performanceTiming}
      />
    {/if}

    <!-- Il tuo kit — blush, eyeshadow, eyebrows e lash line sono renderizzati
         sul volto. Le note di adattamento guidano il make-up artist in negozio. -->
    {#if selectedStyleDisplay.kit}
      <section class="card space-y-3">
        <h3 class="font-display text-lg">{copy.kit.title}</h3>
        <div class="grid grid-cols-3 gap-2">
          <div class="flex flex-col items-center gap-1 text-center">
            <span
              class="block h-14 w-14 rounded-full border border-black/10"
              style="background-color: {selectedStyle.hex};"
              aria-hidden="true"
            ></span>
            <span class="text-[11px] font-medium text-brand-ink">{copy.kit.lipstick}</span>
            <span class="text-[10px] text-brand-muted">{selectedStyleDisplay.finish}</span>
          </div>
          <div class="flex flex-col items-center gap-1 text-center">
            <span
              class="block h-14 w-14 rounded-full border border-black/10"
              style="background-color: {selectedStyleDisplay.kit.blush.hex};"
              aria-hidden="true"
            ></span>
            <span class="text-[11px] font-medium text-brand-ink">{copy.kit.blush}</span>
            <span class="text-[10px] text-brand-muted">{selectedStyleDisplay.kit.blush.finish}</span>
          </div>
          <div class="flex flex-col items-center gap-1 text-center">
            <span
              class="block h-14 w-14 rounded-full border border-black/10"
              style="background-color: {selectedStyleDisplay.kit.eyeshadow.hex};"
              aria-hidden="true"
            ></span>
            <span class="text-[11px] font-medium text-brand-ink">{copy.kit.eyeshadow}</span>
            <span class="text-[10px] text-brand-muted">{selectedStyleDisplay.kit.eyeshadow.finish}</span>
          </div>
        </div>

        <details class="text-[12px] leading-relaxed text-brand-muted">
          <summary class="cursor-pointer text-[11px] font-medium text-brand-accent">
            {copy.kit.notes}
          </summary>
          <div class="mt-2 space-y-1.5">
            <p><strong class="text-brand-ink">{copy.kit.lipstick}.</strong> {selectedStyleDisplay.kit.lipstickNote}</p>
            <p><strong class="text-brand-ink">{copy.kit.blush}.</strong> {selectedStyleDisplay.kit.blush.note}</p>
            <p><strong class="text-brand-ink">{copy.kit.eyeshadow}.</strong> {selectedStyleDisplay.kit.eyeshadow.note}</p>
          </div>
        </details>
      </section>
    {/if}

    {#if hasCrossBrandMatches()}
      <section class="card space-y-3" data-testid="cross-brand-matches">
        <div class="flex items-start justify-between gap-3">
          <div class="min-w-0">
            <h3 class="font-display text-lg">{copy.crossBrand.title}</h3>
            <p class="text-[12px] leading-relaxed text-brand-muted">
              {copy.crossBrand.body}
            </p>
          </div>
          {#if lookPageUrl}
            <a
              href={lookPageUrl}
              class="inline-flex min-h-[44px] shrink-0 items-center rounded-pill border border-brand-accent px-3 py-1.5 text-[11px] font-semibold text-brand-accent transition hover:bg-brand-highlight"
              data-testid="vto-view-look-link"
              onclick={onViewLookClick}
            >
              {copy.crossBrand.viewLook}
            </a>
          {/if}
        </div>

        <div class="space-y-4">
          {#each CROSS_BRAND_COMPONENTS as component}
            {@const items = crossBrandMatchesFor(component.id)}
            {#if items.length > 0}
              <div class="space-y-1.5">
                <p class="text-[11px] font-semibold uppercase tracking-wider text-brand-accent">
                  {copy.components[component.id]}
                </p>
                {#each items as item (item.sku ?? item.shade_name + item.brand)}
                  {@const quality = matchQuality(item)}
                  {@const buyUrl = matchBuyUrl(item)}
                  <article class="grid grid-cols-[auto_1fr_auto] items-center gap-3 rounded-md border border-brand-highlight bg-white px-3 py-2">
                    <span
                      class="block h-8 w-8 rounded-full border border-black/10"
                      style="background-color: {item.hex};"
                      aria-hidden="true"
                    ></span>
                    <div class="min-w-0">
                      <p class="text-[10px] font-semibold uppercase tracking-wider text-brand-accent">
                        {brandLabel(item.brand)}
                      </p>
                      <p class="truncate text-[12px] font-semibold text-brand-ink">
                        {item.shade_name}
                      </p>
                      <p class="truncate text-[11px] text-brand-muted">
                        {item.product_name}{#if item.price != null} · {item.price} {item.currency ?? ''}{/if}
                      </p>
                    </div>
                    <div class="flex flex-col items-end gap-1">
                      <span class="rounded-pill px-2 py-0.5 text-[10px] font-semibold {MATCH_QUALITY_CLASS[quality]}">
                        {copy.crossBrand.quality[quality]}
                      </span>
                      <span class="text-[10px] font-medium text-brand-muted">
                        ΔE {item.delta_e.toFixed(1)}
                      </span>
                      {#if buyUrl}
                        <a
                          href={buyUrl}
                          target="_blank"
                          rel="noopener noreferrer sponsored"
                          class="mt-0.5 inline-flex min-h-[44px] items-center rounded-pill bg-brand-accent px-3 py-1.5 text-[11px] font-semibold text-white shadow-sm transition hover:bg-brand-accentSoft"
                          onclick={() => onMatchBuyClick(component.id, item)}
                        >
                          {copy.crossBrand.buy}
                        </a>
                      {/if}
                    </div>
                  </article>
                {/each}
              </div>
            {/if}
          {/each}
        </div>
      </section>
    {/if}

    {#if selectedStyleDisplay.cosmeticMap}
      {@const map = selectedStyleDisplay.cosmeticMap}
      <section class="card space-y-3">
        <div class="flex items-start justify-between gap-3">
          <div>
            <h3 class="font-display text-lg">{copy.lookCard.title}</h3>
            <p class="mt-1 text-[13px] leading-relaxed text-brand-ink">{map.intent}</p>
          </div>
          <button
            type="button"
            class="min-h-[40px] shrink-0 rounded-pill bg-brand-accent px-3 py-2 text-[11px] font-semibold text-white shadow-sm transition hover:bg-brand-accentSoft disabled:opacity-60"
            onclick={onShareLookCardClick}
            disabled={cardSharing}
          >
            {cardSharing ? copy.lookCard.preparing : copy.lookCard.export}
          </button>
        </div>

        <div class="grid grid-cols-2 gap-2 text-[11px] leading-tight">
          <div class="rounded-md bg-brand-bg px-3 py-2">
            <span class="block font-semibold uppercase tracking-wider text-brand-accent">{copy.lookCard.code}</span>
            <span class="mt-1 block text-brand-ink">{selectedStyle.id}</span>
          </div>
          <div class="rounded-md bg-brand-bg px-3 py-2">
            <span class="block font-semibold uppercase tracking-wider text-brand-accent">{copy.lookCard.palette}</span>
            <span class="mt-1 block text-brand-ink">{map.paletteFamily}</span>
          </div>
          <div class="rounded-md bg-brand-bg px-3 py-2">
            <span class="block font-semibold uppercase tracking-wider text-brand-accent">{copy.lookCard.temperature}</span>
            <span class="mt-1 block text-brand-ink">{map.temperature}</span>
          </div>
          <div class="rounded-md bg-brand-bg px-3 py-2">
            <span class="block font-semibold uppercase tracking-wider text-brand-accent">{copy.lookCard.contrast}</span>
            <span class="mt-1 block text-brand-ink">{map.contrast}</span>
          </div>
          <div class="rounded-md bg-brand-bg px-3 py-2">
            <span class="block font-semibold uppercase tracking-wider text-brand-accent">{copy.lookCard.chroma}</span>
            <span class="mt-1 block text-brand-ink">{map.chroma}</span>
          </div>
          <div class="rounded-md bg-brand-bg px-3 py-2">
            <span class="block font-semibold uppercase tracking-wider text-brand-accent">{copy.lookCard.skinFit}</span>
            <span class="mt-1 block text-brand-ink">{map.skinDepthFit}</span>
          </div>
        </div>

        <div class="flex flex-wrap gap-1.5">
          {#each map.focus as item}
            <span class="rounded-pill bg-brand-highlight px-2.5 py-1 text-[11px] font-semibold text-brand-accent">
              {item}
            </span>
          {/each}
        </div>

        <dl class="grid grid-cols-2 gap-2 text-[11px] leading-relaxed text-brand-muted">
          <div>
            <dt class="font-semibold text-brand-ink">{copy.lookCard.skin}</dt>
            <dd>{map.finishProfile.skin}</dd>
          </div>
          <div>
            <dt class="font-semibold text-brand-ink">{copy.lookCard.lips}</dt>
            <dd>{map.finishProfile.lips}</dd>
          </div>
          <div>
            <dt class="font-semibold text-brand-ink">{copy.lookCard.eyes}</dt>
            <dd>{map.finishProfile.eyes}</dd>
          </div>
          <div>
            <dt class="font-semibold text-brand-ink">{copy.lookCard.cheeks}</dt>
            <dd>{map.finishProfile.cheeks}</dd>
          </div>
        </dl>

        <details class="text-[12px] leading-relaxed text-brand-muted">
          <summary class="cursor-pointer text-[11px] font-medium text-brand-accent">
            {copy.lookCard.counterBrief}
          </summary>
          <div class="mt-2 space-y-1.5">
            <p><strong class="text-brand-ink">{copy.lookCard.eyes}.</strong> {map.placement.eyes}</p>
            <p><strong class="text-brand-ink">{copy.lookCard.cheeks}.</strong> {map.placement.cheeks}</p>
            <p><strong class="text-brand-ink">{copy.lookCard.lips}.</strong> {map.placement.lips}</p>
            <p><strong class="text-brand-ink">{copy.lookCard.avoid}.</strong> {map.counterBrief.join(', ')}</p>
            <p><strong class="text-brand-ink">{copy.lookCard.controls}.</strong> {map.riskControls.join(', ')}</p>
          </div>
        </details>
      </section>
    {/if}

    <!-- Rationale — mini-brief editoriale per consumer e make-up artist -->
    {#if selectedStyleDisplay.rationale}
      <section class="card space-y-3">
        <h3 class="font-display text-lg">{copy.rationale.title}</h3>
        <p class="text-[13px] leading-relaxed text-brand-ink">{selectedStyleDisplay.rationale.whyItWorks}</p>
        <dl class="space-y-2 text-[12px] leading-relaxed text-brand-muted">
          <div>
            <dt class="text-[11px] font-semibold uppercase tracking-wider text-brand-accent">{copy.rationale.forWho}</dt>
            <dd class="mt-0.5">{selectedStyleDisplay.rationale.skinFit}</dd>
          </div>
          <div>
            <dt class="text-[11px] font-semibold uppercase tracking-wider text-brand-accent">{copy.rationale.when}</dt>
            <dd class="mt-0.5">{selectedStyleDisplay.rationale.occasionFit}</dd>
          </div>
          <div>
            <dt class="text-[11px] font-semibold uppercase tracking-wider text-brand-accent">{copy.rationale.avoid}</dt>
            <dd class="mt-0.5">{selectedStyleDisplay.rationale.avoid}</dd>
          </div>
        </dl>
        <p class="rounded-md bg-brand-bg px-3 py-2 text-[11px] italic leading-relaxed text-brand-muted">
          {copy.rationale.storeHint}
        </p>
      </section>
    {/if}

    <section class="card space-y-3">
      <h3 class="font-display text-lg">{copy.beautyPlayers.title}</h3>
      <p class="text-[12px] leading-relaxed text-brand-muted">
        {copy.beautyPlayers.body}
      </p>
      <div class="grid grid-cols-2 gap-2">
        {#each BEAUTY_PLAYERS as player}
          <a
            href={player.url}
            target="_blank"
            rel="noopener noreferrer"
            class="rounded-md border border-brand-highlight bg-white px-3 py-2 text-center text-xs font-semibold text-brand-ink transition hover:border-brand-accent hover:text-brand-accent"
            onclick={() => {
              track('beauty_player_clicked', {
                style: selectedStyle?.id ?? '',
                player: player.name
              });
            }}
          >
            {player.name}
          </a>
        {/each}
      </div>
    </section>

    {#if (quality && quality.warnings.length > 0) || (lightingCalibration && lightingCalibration.warnings.length > 0) || (skinToneCalibration && skinToneCalibration.warnings.length > 0)}
      <section class="card space-y-2 border border-brand-accent/20 bg-brand-highlight/25 p-4">
        <p class="text-[11px] font-semibold uppercase tracking-wider text-brand-accent">
          {copy.result.betterResult}
        </p>
        <ul class="ml-4 list-disc space-y-1 text-[12px] leading-relaxed text-brand-ink">
          {#if quality}
            {#each quality.warnings as warning (warning)}
              <li>{qualityWarningText(warning)}</li>
            {/each}
          {/if}
          {#if lightingCalibration}
            {#each lightingCalibration.warnings as warning (warning)}
              <li>{qualityWarningText(warning)}</li>
            {/each}
          {/if}
          {#if skinToneCalibration}
            {#each skinToneCalibration.warnings as warning (warning)}
              <li>{qualityWarningText(warning)}</li>
            {/each}
          {/if}
        </ul>
      </section>
    {/if}

    <!-- Actions -->
    <div class="flex flex-col gap-2">
      {#if PREMIUM_RENDER_ENABLED}
      <button
        class="w-full rounded-pill border border-brand-accent/40 bg-brand-surface px-3 py-3 text-sm font-semibold text-brand-accent shadow-sm"
        onclick={requestPremiumRender}
        disabled={premiumRendering || sharing}
        data-testid="premium-render-button"
      >
        {premiumRendering ? copy.result.premiumRendering : copy.result.premiumButton}
      </button>
      {/if}
      {#if premiumRenderStatus}
        <p class="rounded-md bg-brand-bg px-3 py-2 text-center text-[11px] leading-relaxed text-brand-muted" aria-live="polite">
          {premiumRenderStatus}
        </p>
      {/if}
      {#if premiumRenderError}
        <p class="rounded-md bg-red-50 px-3 py-2 text-center text-[11px] font-semibold leading-relaxed text-red-700" aria-live="polite">
          {premiumRenderError}
        </p>
      {/if}
      <p class="text-center text-[11px] leading-relaxed text-brand-muted">
        {copy.result.premiumPrivacy}
      </p>
      <button
        class="btn-primary w-full"
        onclick={onShareClick}
        disabled={sharing}
      >
        {sharing ? copy.result.sharing : copy.result.share}
      </button>
      <!-- Ricomincia = reset everything (new photo, first look). The per-look
           navigation is already handled by the strip above, so we drop the
           old "Altri look" button. -->
      <button
        class="min-h-[44px] w-full rounded-pill border border-brand-accent/30 px-3 py-2 text-xs text-brand-accent"
        onclick={retakePhoto}
      >
        {copy.result.newPhoto}
      </button>
    </div>
  {/if}

  {#if rendererDebug}
    <RendererDebug />
  {/if}

  <footer
    class={`flex flex-col items-center gap-2 ${
      phase === 'landing' ? 'mt-1 pb-2' : 'mt-auto pt-4'
    }`}
  >
    <a
      href="/index.html"
      class={`inline-flex items-center justify-center rounded-pill no-underline transition ${
        phase === 'landing'
          ? 'min-h-11 w-full border border-brand-accent/70 bg-brand-surface px-5 py-3 text-sm font-semibold text-brand-accent shadow-sm ring-1 ring-brand-accent/10 hover:border-brand-accent'
          : 'border border-brand-accent bg-brand-accent px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-brand-accentSoft'
      }`}
    >
      {copy.common.backToBeautyIQ}
    </a>
    <p class="text-[11px] text-brand-muted">v0.4.7 · {new Date().getFullYear()}</p>
  </footer>
</main>
